<template>
  <div>
    <div v-if="!pageReady" class="text-center" style="padding: 80px 0;">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando...</span>
      </div>
      <p class="mt-3 text-muted">Carregando configurações...</p>
    </div>
    <div v-if="pageReady" class="card">
      <div class="card-header animated fadeInRightBig">
        <div class="card-title">{{ isUserView ? 'Minhas Escalas' : 'Escalas de Trabalho' }}</div>
          <div v-if="importInBackground" style="margin-left: auto; display:flex; gap:8px; align-items:center; flex-wrap: wrap;">
            <div class="alert alert-info mb-0" style="padding:6px 10px; display:flex; align-items:center; gap:10px;">
              <i class="fas fa-hourglass-half"></i>
              <div style="font-size:0.9rem;">Importação em andamento em segundo plano</div>
              <button class="btn btn-sm btn-outline-light" @click.prevent="handleImportCompletion(importJobId)">Ver resultados</button>
              <button class="btn btn-sm btn-light" @click.prevent="dismissImportBanner">Fechar</button>
            </div>
          </div>
        <div v-if="isUserView" style="margin-left: auto; display:flex; gap:8px; align-items:center; flex-wrap: wrap;">
          <a href="/WorkSchedulesSwap" target="_blank" class="btn btn-outline-primary" style="position:relative;">
            <i class="fas fa-exchange-alt"></i>
            Trocar Escala
            <span v-if="swapPendingCount"
                  style="position:absolute;top:-6px;right:-6px;background:#ea5455;color:#fff;border-radius:50%;min-width:17px;height:17px;font-size:10px;display:flex;align-items:center;justify-content:center;font-weight:bold;padding:0 3px;">
              {{ swapPendingCount }}
            </span>
          </a>          
          <button v-if="isSwapApprovalEnabled" @click="openRequestShiftModal" class="btn btn-info">
            <i class="fas fa-hand-paper"></i>
            Pegar Plantão
          </button>
        </div>
        <div v-if="!isUserView" style="margin-left: auto; display:flex; gap:8px; align-items:center; flex-wrap: wrap;">
          <a href="/WorkSchedulesSwap" target="_blank" class="btn btn-outline-primary" style="position:relative;">
            <i class="fas fa-exchange-alt"></i>
            Trocas de Escala
            <span v-if="swapPendingCount"
                  style="position:absolute;top:-6px;right:-6px;background:#ea5455;color:#fff;border-radius:50%;min-width:17px;height:17px;font-size:10px;display:flex;align-items:center;justify-content:center;font-weight:bold;padding:0 3px;">
              {{ swapPendingCount }}
            </span>
          </a>
          <button @click="openImportModal" class="btn btn-secondary" :disabled="isImportBlocked" :title="isImportBlocked ? 'Aguarde a conclusão da importação atual' : ''">
            <i class="fas fa-file-import"></i>
            Importar
          </button>
          <button @click="openScheduleModal" class="btn btn-primary">
            <i class="fas fa-plus"></i>
            Nova Escala
          </button>
          <button v-if="!selectionMode" @click="toggleSelectionMode" class="btn btn-outline-danger">
            <i class="far fa-trash-alt"></i>
            Excluir em massa
          </button>
          <template v-else>
            <button @click="bulkDeleteSelected" class="btn btn-danger" :disabled="selectedScheduleIds.length === 0">
              <i class="far fa-trash-alt"></i>
              Excluir selecionadas ({{ selectedScheduleIds.length }})
            </button>
            <button @click="toggleSelectionMode" class="btn btn-light">Cancelar</button>
          </template>
        </div>
      </div>

      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-3">
            <label>Período</label>
            <select v-model="selectedMonth" @change="onMonthChange" class="form-control">
              <option v-for="month in months" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>

          <div class="col-md-3" v-if="!isUserView">
            <label>Departamento</label>
            <select v-model="selectedDepartment" @change="onDepartmentChange" class="form-control">
              <option value="">Todos</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                {{ dept.title }}
              </option>
            </select>
          </div>

          <div class="col-md-3" v-if="!isUserView">
            <label>Colaborador</label>
            <v-select
              v-model="selectedEmployee"
              :options="filteredEmployees"
              label="name"
              :reduce="emp => emp.id"
              :placeholder="selectedDepartment ? 'Digite para pesquisar' : 'Selecione departamento'"
              :disabled="!selectedDepartment"
              :loading="loadingEmployees && employeeContextType === 'filter'"
              clearable
              @open="onFilterEmployeeOpen"
              @input="filterSchedules"
            />
            <small v-if="selectedDepartment && employeeContextType === 'filter' && filteredEmployees.length" class="text-muted d-block mt-1">
              {{ filteredEmployees.length }} colaborador(es) carregado(s)
            </small>
          </div>

          <div class="col-md-3">
            <label>Visualização</label>
            <select v-model="calendarView" @change="changeView" class="form-control">
              <option value="dayGridMonth">Mensal</option>
              <option value="timeGridWeek">Semanal</option>
            </select>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-8">
            <div class="nav-controls" style="display: flex; align-items: center; gap: 10px;">
              <button @click="goToPrev" class="btn btn-default btn-sm">‹</button>
              <button @click="goToToday" class="btn btn-default btn-sm">Hoje</button>
              <button @click="goToNext" class="btn btn-default btn-sm">›</button>
              <span class="period-display"
                    style="margin-left: 15px; font-size: 16px; font-weight: 600;">{{ currentPeriodName }}</span>
            </div>
          </div>

          <div class="col-md-4 text-right">
            <div class="legend" style="display: flex; gap: 15px; justify-content: flex-end; align-items: center; flex-wrap: wrap;">
              <div class="legend-item" style="display: flex; align-items: center; gap: 6px;">
                <div class="legend-dot" style="width: 10px; height: 10px; border-radius: 50%; background: #007bff;"></div>
                <span style="font-size: 12px;">Normal</span>
              </div>
              <div class="legend-item" style="display: flex; align-items: center; gap: 6px;">
                <div class="legend-dot" style="width: 10px; height: 10px; border-radius: 50%; background: #28a745;"></div>
                <span style="font-size: 12px;">Hora Extra</span>
              </div>
              <div class="legend-item" style="display: flex; align-items: center; gap: 6px;">
                <div class="legend-dot" style="width: 10px; height: 10px; border-radius: 50%; background: #FF9800;"></div>
                <span style="font-size: 12px;">Trocado</span>
              </div>
              <div class="legend-item" style="display: flex; align-items: center; gap: 6px;">
                <div class="legend-dot" style="width: 10px; height: 10px; border-radius: 50%; background: #8e44ad;"></div>
                <span style="font-size: 12px;">Plantão</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isImportInBackground" class="row mb-3">
          <div class="col-12">
            <div class="alert alert-info d-flex align-items-center justify-content-between" role="alert" style="gap:12px;">
              <div style="display:flex; align-items:center; gap:12px;">
                <i class="fas fa-hourglass-half fa-lg"></i>
                <div>
                  <div style="font-weight:600">Importação em andamento</div>
                  <div style="font-size:0.9rem; color:#495057;">{{ importStatus }} <span v-if="importProgressPercent"> — {{ importProgressPercent }}%</span></div>
                </div>
              </div>
              <div style="display:flex; gap:8px; align-items:center;">
                <button class="btn btn-sm btn-outline-light" @click.prevent="handleImportCompletion(importJobId)">Ver resultados</button>
                <button class="btn btn-sm btn-light" @click.prevent="dismissImportBanner">Fechar</button>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div style="position: relative;">
              <div v-if="loading" class="text-center"
                   style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; background: rgba(255,255,255,0.7); z-index: 10;">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Carregando escalas...</span>
                </div>
                <p class="mt-2">Carregando escalas...</p>
              </div>

              <div v-if="selectionMode" class="alert alert-warning py-2 mb-2" role="status">
                <i class="fas fa-hand-pointer mr-1"></i>
                Modo de exclusão em massa: clique nas escalas para selecionar (incluindo plantões). Depois clique em "Excluir selecionadas".
              </div>

              <FullCalendar ref="fullCalendar" :options="calendarOptions"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showMultipleEventsModal" class="custom-modal-overlay">
      <div class="modal-dialog" role="document" style="max-width: 760px; width: 100%;">
        <div class="modal-content import-modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ multipleEventsModalTitle }}</h5>
            <button type="button" class="close" @click="closeMultipleEventsModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body" style="padding: 20px;">
            <div class="form-group mb-3">
              <input
                v-model="multipleEventsSearch"
                type="text"
                class="form-control"
                placeholder="Pesquisar por colaborador ou departamento"
              >
              <small class="text-muted">Encontrados: {{ filteredMultipleEvents.length }} de {{ multipleEventsData.length }} escalas</small>
            </div>

            <div style="max-height: 420px; overflow-y: auto; padding-right: 4px;">
              <div
                v-for="event in filteredMultipleEvents"
                :key="event.id"
                class="border rounded mb-2"
                style="padding: 12px; cursor: pointer; transition: background 0.2s ease;"
                @click="selectEventFromList(event)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="d-flex align-items-center" style="gap: 8px;">
                      <span class="badge" :style="{ backgroundColor: event.color, color: '#fff' }">
                        {{ event.extendedProps.is_swapped ? 'Trocado' : (event.extendedProps.is_overtime ? 'HE' : 'Normal') }}
                      </span>
                      <strong>{{ getEmployeeNameFromEvent(event) }}</strong>
                    </div>
                    <div class="text-muted mt-1" style="font-size: 12px;">
                      {{ getDepartmentNameFromEventData(event) }}
                      <span v-if="event.extendedProps.is_swapped"> • Original: {{ getOriginalEmployeeNameFromEventData(event) }}</span>
                    </div>
                  </div>
                  <i class="fas fa-chevron-right text-muted"></i>
                </div>
              </div>

              <div v-if="filteredMultipleEvents.length === 0" class="text-center text-muted py-4">
                <i class="fas fa-search" style="font-size: 24px; opacity: 0.5;"></i>
                <p class="mb-0 mt-2">Nenhuma escala encontrada com este filtro</p>
              </div>
            </div>
          </div>

          <div class="modal-footer import-modal-footer">
            <div class="left-actions"></div>
            <div class="right-actions">
              <button type="button" class="btn btn-secondary" @click="closeMultipleEventsModal">Fechar</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showScheduleModal && !isUserView" class="modal fade show" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog" role="document" style="max-width: 700px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ isQuickSchedule ? `Nova Escala - ${formatDate(quickScheduleDate)}` : 'Nova Escala' }}</h5>
            <button type="button" class="close" @click="closeScheduleModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body" style="padding: 20px;">
            <form @submit.prevent="saveSchedule">
              <div class="row">
                <div class="col-md-6" v-if="!isQuickSchedule">
                  <div class="form-group">
                    <label><span class="required">*</span> Mês/Ano</label>
                    <input type="month" v-model="newSchedule.month" class="form-control" required>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label><span class="required">*</span> Departamento</label>
                    <select v-model="newSchedule.departmentId" @change="onModalDepartmentChange" class="form-control"
                            required>
                      <option value="">Selecione</option>
                      <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.title }}</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label><span class="required">*</span> Colaborador</label>
                    <v-select
                      v-model="newSchedule.employeeId"
                      :options="modalEmployees"
                      label="name"
                      :reduce="emp => emp.id"
                      :placeholder="newSchedule.departmentId ? 'Digite para pesquisar' : 'Primeiro selecione departamento'"
                      :disabled="!newSchedule.departmentId"
                      :loading="loadingEmployees && employeeContextType === 'modal'"
                      @open="onModalEmployeeOpen"
                    />
                    <small v-if="newSchedule.departmentId && employeeContextType === 'modal' && modalEmployees.length" class="text-muted d-block mt-1">
                      {{ modalEmployees.length }} colaborador(es) carregado(s)
                    </small>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mt-2">
                    <div class="custom-control custom-switch">
                      <input
                        id="is-overtime-schedule"
                        v-model="newSchedule.isOvertime"
                        type="checkbox"
                        class="custom-control-input"
                      >
                      <label class="custom-control-label" for="is-overtime-schedule">Marcar como hora extra</label>
                    </div>
                  </div>
                </div>

                <template v-if="newSchedule.employeeId">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Início do turno</label>
                      <div v-if="loadingModalWorktime" class="form-control text-muted d-flex align-items-center" style="gap:6px;">
                        <i class="fas fa-spinner fa-spin" style="font-size:0.8rem;"></i> carregando...
                      </div>
                      <div v-else class="input-group">
                        <div v-if="newScheduleShiftDates" class="input-group-prepend">
                          <span class="input-group-text" style="font-size:0.82rem; padding: 0 8px; white-space:nowrap;">
                            {{ newScheduleShiftDates.startDate.slice(0,5) }}
                          </span>
                        </div>
                        <input type="time" v-model="newSchedule.shiftStartTime" class="form-control" />
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Fim do turno</label>
                      <div v-if="loadingModalWorktime" class="form-control text-muted d-flex align-items-center" style="gap:6px;">
                        <i class="fas fa-spinner fa-spin" style="font-size:0.8rem;"></i> carregando...
                      </div>
                      <div v-else class="input-group">
                        <div v-if="newScheduleShiftDates" class="input-group-prepend">
                          <span class="input-group-text" style="font-size:0.82rem; padding: 0 8px; white-space:nowrap;"
                                :class="newScheduleShiftDates.endDate !== newScheduleShiftDates.startDate ? 'text-warning font-weight-bold' : ''">
                            {{ newScheduleShiftDates.endDate.slice(0,5) }}
                          </span>
                        </div>
                        <input type="time" v-model="newSchedule.shiftEndTime" class="form-control" />
                      </div>
                    </div>
                  </div>
                </template>

              </div>

              <div v-if="!isQuickSchedule" class="form-group">
                <label><span class="required">*</span> Dias de Trabalho</label>
                <div class="days-selector-compact"
                     style="display: grid; grid-template-columns: repeat(10, 1fr); gap: 4px; padding: 10px; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; max-height: 200px; overflow-y: auto;">
                  <div v-for="day in monthDays" :key="day" @click="toggleWorkDay(day)"
                       :class="['day-item-compact', { selected: newSchedule.workDays.includes(day) }]"
                       style="display: flex; align-items: center; justify-content: center; background: white; border: 1px solid #dee2e6; border-radius: 3px; cursor: pointer; font-weight: 500; font-size: 12px; height: 28px; transition: all 0.2s ease;">
                    {{ day }}
                  </div>
                </div>
              </div>

              <div class="form-group mb-0 mt-4"
                   style="display: flex; justify-content: space-between; align-items: center;">
                <small class="text-muted"><span class="required">*</span> Campos Obrigatórios</small>
                <div>
                  <button type="button" @click="closeScheduleModal" class="btn btn-back">
                    <i class="fa fa-times"></i> Cancelar
                  </button>
                  <button type="submit" class="btn btn-add ml-2" :disabled="saving">
                    <span v-if="saving">
                      <i class="fa fa-spinner fa-spin"></i> Salvando...
                    </span>
                    <span v-else>
                      Salvar <i class="fa fa-save"></i>
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showEventModal && selectedSwapEvent" class="modal fade show" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog" role="document" style="max-width: 600px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isUserView ? 'Minha Escala' : 'Gerenciar Escala' }}</h5>
            <button type="button" class="close" @click="closeEventModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body" style="padding: 20px;">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Data</strong></label>
                  <div class="form-control-plaintext">{{ formatDate(selectedSwapEvent.start) }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Departamento</strong></label>
                  <div class="form-control-plaintext">{{ getDepartmentNameFromEvent() }}</div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Colaborador</strong></label>
                  <div class="form-control-plaintext">
                    {{ getDisplayEmployeeName() }}
                    <span v-if="selectedSwapEvent.extendedProps.is_swapped"
                          class="badge badge-warning ml-1">Trocado</span>
                    <span v-if="selectedSwapEvent.extendedProps.is_overtime"
                          class="badge badge-success ml-1">Hora Extra</span>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Horário</strong></label>
                  <div v-if="eventShiftTimes" class="form-control-plaintext">
                    <div><span class="text-muted" style="font-size:0.82rem;">Início:</span> {{ eventShiftTimes.start }} <span class="text-muted" style="font-size:0.82rem;">{{ eventShiftTimes.startDate }}</span></div>
                    <div><span class="text-muted" style="font-size:0.82rem;">Fim:</span> {{ eventShiftTimes.end }} <span class="text-muted" style="font-size:0.82rem;">{{ eventShiftTimes.endDate }}</span></div>
                  </div>
                  <div v-else class="form-control-plaintext text-muted">—</div>
                </div>
              </div>
            </div>

            <div v-if="selectedSwapEvent.extendedProps.is_swapped" class="alert alert-warning mt-3">
              <h6><strong>Detalhes da Troca</strong></h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-2">
                    <label><strong>Colaborador Original</strong></label>
                    <div class="form-control-plaintext">{{ getOriginalEmployeeName() }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-2">
                    <label><strong>Motivo</strong></label>
                    <div class="form-control-plaintext">{{ selectedSwapEvent.extendedProps.swap_reason }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!isUserView" class="mt-4">
              <div v-if="currentEventReleaseRequest && currentEventReleaseRequest.status === 'pending'"
                   class="alert alert-warning mb-3" style="padding: 8px 12px; font-size: 0.85rem;">
                <div class="d-flex align-items-center" style="gap: 6px;">
                  <i class="fas fa-exclamation-triangle"></i>
                  <strong>Solicitação de liberação pendente</strong>
                </div>
                <div class="text-muted mt-1">
                  <strong>{{ currentEventReleaseRequest.user && currentEventReleaseRequest.user.name }}</strong>
                  solicitou liberação de
                  <strong>{{ currentEventReleaseRequest.proposed_start_time }} – {{ currentEventReleaseRequest.proposed_end_time }}</strong>.
                </div>
                <div v-if="currentEventReleaseRequest.reason" class="mt-1">
                  Motivo: <em>{{ currentEventReleaseRequest.reason }}</em>
                </div>
                <div class="text-muted mt-1" style="font-size: 0.8rem;">Gerencie na tela de Plantões.</div>
              </div>
              <div class="d-flex justify-content-between" style="gap: 8px;">
                <template v-if="!isSwapApprovalEnabled">
                  <button @click="confirmDeleteEvent" class="btn btn-outline-danger">
                    <i class="fas fa-trash-alt"></i> Excluir escala
                  </button>
                </template>
                <template v-else>
                  <button @click="confirmDeleteEvent" class="btn btn-outline-danger">
                    <i class="fas fa-trash-alt"></i> Excluir escala
                  </button>
                  <div class="d-flex" style="gap: 8px;">
                    <button @click="openEditScheduleModal" class="btn btn-outline-secondary">
                      <i class="fas fa-pencil-alt"></i> Editar escala
                    </button>
                    <button @click="openReleaseModal('direct')" class="btn btn-warning">
                      <i class="fas fa-unlock"></i> Liberar e criar plantão
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="isUserView" class="form-group mb-0 mt-4"
                 style="display: flex; justify-content: flex-end; align-items: center; gap: 8px;">
              <template v-if="selectedSwapEvent.extendedProps.isDutyShift">
                <button
                  @click="releaseApprovedDutyShift"
                  class="btn btn-warning"
                  :disabled="cancelingShiftId === selectedSwapEvent.extendedProps.duty_shift_allocation_id"
                >
                  <span v-if="cancelingShiftId === selectedSwapEvent.extendedProps.duty_shift_allocation_id">
                    <i class="fa fa-spinner fa-spin"></i> Liberando...
                  </span>
                  <span v-else><i class="fas fa-unlock"></i> Liberar plantão</span>
                </button>
              </template>
              <template v-else-if="isSwapApprovalEnabled">
                <template v-if="currentEventReleaseRequest && currentEventReleaseRequest.status === 'rejected'">
                  <div class="w-100 mb-2">
                    <div class="alert alert-danger mb-2" style="font-size: 0.85rem; padding: 8px 12px;">
                      <div class="d-flex align-items-center" style="gap: 6px;">
                        <i class="fas fa-times-circle"></i>
                        <strong>Solicitação recusada</strong>
                      </div>
                      <div v-if="currentEventReleaseRequest.rejection_reason" class="mt-1">
                        Justificativa: {{ currentEventReleaseRequest.rejection_reason }}
                      </div>
                    </div>
                    <div class="d-flex justify-content-end">
                      <button @click="requestRelease" class="btn btn-warning btn-sm">
                        <i class="fas fa-unlock"></i> Nova solicitação
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else-if="currentEventReleaseRequest && currentEventReleaseRequest.status === 'pending'">
                  <span class="badge badge-warning p-2" style="font-size: 0.85rem;">
                    <i class="fas fa-hourglass-half"></i> Liberação pendente
                  </span>
                  <button @click="cancelReleaseRequest" class="btn btn-outline-danger btn-sm">
                    Cancelar solicitação
                  </button>
                </template>
                <button v-else @click="requestRelease" class="btn btn-warning">
                  <i class="fas fa-unlock"></i> Solicitar liberação
                </button>
              </template>
              <template v-else>
                <button
                  @click="releaseOwnSchedule"
                  class="btn btn-warning"
                  :disabled="releasingSchedule"
                >
                  <span v-if="releasingSchedule"><i class="fa fa-spinner fa-spin"></i> Liberando...</span>
                  <span v-else><i class="fas fa-unlock"></i> Liberar escala</span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: liberar escala e criar plantão / aprovar liberação -->
    <div v-if="showReleaseModal && selectedSwapEvent" class="modal fade show" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0,0,0,0.5); z-index: 1060;">
      <div class="modal-dialog" role="document" style="max-width: 560px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-unlock mr-2"></i>
              {{ approvingReleaseRequest ? 'Aprovar liberação' : 'Liberar escala e criar plantão' }}
            </h5>
            <button type="button" class="close" @click="showReleaseModal = false"><span>&times;</span></button>
          </div>
          <div class="modal-body" style="padding: 20px;">

            <!-- MODO APROVAR: mostra horários original/proposto/restante + título -->
            <template v-if="approvingReleaseRequest">
              <div class="mb-3 p-2 bg-light rounded" style="font-size: 0.88rem;">
                <strong>{{ getDisplayEmployeeName() }}</strong> — {{ formatDate(selectedSwapEvent.start) }}
              </div>
              <div class="row mb-2" style="font-size: 0.88rem;">
                <div class="col-6">
                  <div class="text-muted">Horário original</div>
                  <strong v-if="eventShiftTimes">{{ eventShiftTimes.start }} – {{ eventShiftTimes.end }}</strong>
                  <span v-else class="text-muted">—</span>
                </div>
                <div class="col-6">
                  <div class="text-muted">Horário proposto</div>
                  <strong>{{ approvingReleaseRequest.proposed_start_time }} – {{ approvingReleaseRequest.proposed_end_time }}</strong>
                </div>
              </div>
              <div v-if="approveModalRemainingSlot" class="alert alert-info mb-2" style="font-size: 0.85rem; padding: 8px 12px;">
                <i class="fas fa-info-circle mr-1"></i>
                Horário restante:
                <div class="mt-1">
                  <strong>{{ approveModalRemainingSlot.startDate }} {{ approveModalRemainingSlot.start }}</strong>
                  <span class="text-muted mx-1">até</span>
                  <strong>{{ approveModalRemainingSlot.endDate }} {{ approveModalRemainingSlot.end }}</strong>
                </div>
              </div>

              <!-- Checkbox: criar plantão -->
              <div class="custom-control custom-switch mb-3">
                <input type="checkbox" class="custom-control-input" id="approve-create-plantao"
                       v-model="approveReleaseForm.create_plantao" />
                <label class="custom-control-label" for="approve-create-plantao" style="font-size: 0.9rem;">
                  Criar plantão para o horário restante
                </label>
              </div>

              <template v-if="approveReleaseForm.create_plantao">
                <div class="form-row">
                  <div class="form-group col-md-8">
                    <label><strong>Título do plantão</strong></label>
                    <input type="text" class="form-control" v-model="approveReleaseForm.plantao_title"
                           placeholder="Ex: Plantão tarde" maxlength="255" />
                  </div>
                  <div class="form-group col-md-4">
                    <label><strong>Vagas</strong></label>
                    <input type="number" class="form-control" v-model.number="approveReleaseForm.plantao_vacancies" min="1" max="99" />
                  </div>
                </div>
                <div v-if="availableRequirements.length" class="form-group mb-0">
                  <label style="font-size: 0.85rem;"><strong>Requisitos</strong> <span class="text-muted">(opcional)</span></label>
                  <div style="max-height: 80px; overflow-y: auto; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 6px 8px;">
                    <div v-for="req in availableRequirements" :key="req.id" class="custom-control custom-checkbox" style="line-height: 1.4;">
                      <input type="checkbox" class="custom-control-input"
                             :id="`approve-req-${req.id}`"
                             :value="req.id"
                             v-model="approveReleaseForm.plantao_requirement_ids" />
                      <label class="custom-control-label" :for="`approve-req-${req.id}`"
                             style="font-size: 0.82rem;">{{ req.name }}</label>
                    </div>
                  </div>
                  <small class="text-muted" style="font-size: 0.78rem;">Sem requisitos = visível para todos</small>
                </div>
              </template>
              <div v-else class="alert alert-warning mb-0" style="font-size: 0.85rem; padding: 8px 12px;">
                <i class="fas fa-exclamation-triangle"></i>
                A liberação será aprovada sem criar nenhum plantão para o horário restante.
              </div>
            </template>

            <!-- MODO DIRETO: libera turno completo e permite dividir em slots -->
            <template v-else>
            <div class="alert alert-info d-flex align-items-center" style="gap: 8px; padding: 8px 12px; font-size: 0.9rem;">
              <i class="fas fa-info-circle"></i>
              <span>A escala de <strong>{{ getDisplayEmployeeName() }}</strong> em <strong>{{ formatDate(selectedSwapEvent.start) }}</strong> será removida e os plantões abaixo serão criados para cobertura.</span>
            </div>

            <div v-if="eventShiftTimes" class="text-muted mb-3" style="font-size: 0.85rem;">
              Horário original: <strong>{{ eventShiftTimes.startDate }} {{ eventShiftTimes.start }}</strong> – <strong>{{ eventShiftTimes.endDate }} {{ eventShiftTimes.end }}</strong>
            </div>

            <div v-for="(slot, index) in releaseSlots" :key="index"
                 class="card mb-2" style="border: 1px solid #dee2e6;">
              <div class="card-body" style="padding: 12px;">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong style="font-size: 0.9rem;">Plantão {{ index + 1 }}</strong>
                  <button type="button" class="btn btn-sm btn-outline-danger"
                          :disabled="releaseSlots.length === 1"
                          @click="removeReleaseSlot(index)">
                    <i class="fas fa-times"></i> Remover
                  </button>
                </div>
                <div class="form-row">
                  <div class="form-group col-12 mb-2">
                    <label style="font-size: 0.85rem;"><strong>Título</strong> <span class="text-muted">(opcional)</span></label>
                    <input type="text" class="form-control form-control-sm" v-model="slot.title"
                           placeholder="Ex: Plantão manhã" maxlength="255" />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-4 mb-0">
                    <label style="font-size: 0.85rem;"><strong>Início</strong></label>
                    <input type="time" class="form-control" v-model="slot.start_time" />
                  </div>
                  <div class="form-group col-md-4 mb-0">
                    <label style="font-size: 0.85rem;"><strong>Fim</strong></label>
                    <input type="time" class="form-control" v-model="slot.end_time" />
                  </div>
                  <div class="form-group col-md-4 mb-0">
                    <label style="font-size: 0.85rem;"><strong>Vagas</strong></label>
                    <input type="number" class="form-control" v-model.number="slot.vacancies"
                           min="1" max="99" />
                  </div>
                </div>
                <div v-if="availableRequirements.length" class="form-group mt-2 mb-0">
                  <label style="font-size: 0.85rem;"><strong>Requisitos</strong> <span class="text-muted">(opcional)</span></label>
                  <div style="max-height: 80px; overflow-y: auto; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 6px 8px;">
                    <div v-for="req in availableRequirements" :key="req.id" class="custom-control custom-checkbox" style="line-height: 1.4;">
                      <input type="checkbox" class="custom-control-input"
                             :id="`slot-req-${index}-${req.id}`"
                             :value="req.id"
                             v-model="slot.requirement_ids" />
                      <label class="custom-control-label" :for="`slot-req-${index}-${req.id}`"
                             style="font-size: 0.82rem;">{{ req.name }}</label>
                    </div>
                  </div>
                  <small class="text-muted" style="font-size: 0.78rem;">Sem requisitos = visível para todos</small>
                </div>
              </div>
            </div>

            <button type="button" class="btn btn-outline-secondary btn-sm mt-1" @click="addReleaseSlot">
              <i class="fas fa-plus"></i> Adicionar divisão de horário
            </button>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showReleaseModal = false">Cancelar</button>
            <button type="button" class="btn btn-warning" :disabled="releasingSchedule" @click="submitRelease">
              <span v-if="releasingSchedule"><i class="fa fa-spinner fa-spin"></i> Liberando...</span>
              <span v-else>
                <i class="fas fa-unlock"></i>
                {{ approvingReleaseRequest ? 'Aprovar' : 'Liberar e criar plantão' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: editar escala (horário + hora extra) -->
    <div v-if="showEditScheduleModal && selectedSwapEvent" class="modal fade show" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0,0,0,0.5); z-index: 1060;">
      <div class="modal-dialog" role="document" style="max-width: 640px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fas fa-pencil-alt mr-2"></i>Editar escala</h5>
            <button type="button" class="close" @click="showEditScheduleModal = false"><span>&times;</span></button>
          </div>
          <div class="modal-body" style="padding: 20px; overflow-y: auto; max-height: calc(100vh - 180px);">
            <div class="mb-3 p-2 bg-light rounded" style="font-size: 0.88rem;">
              <strong>{{ getDisplayEmployeeName() }}</strong> — {{ formatDate(selectedSwapEvent.start) }}
            </div>

            <div v-if="eventShiftTimes" class="mb-3" style="font-size: 0.85rem; color: #6c757d;">
              Horário original:
              <strong>{{ eventShiftTimes.startDate }} {{ eventShiftTimes.start }}</strong>
              –
              <strong>{{ eventShiftTimes.endDate }} {{ eventShiftTimes.end }}</strong>
            </div>

            <div class="form-row">
              <div class="form-group col-md-6">
                <label><strong>Novo início</strong></label>
                <input type="datetime-local" class="form-control" v-model="editScheduleForm.shift_start_time" />
              </div>
              <div class="form-group col-md-6">
                <label><strong>Novo fim</strong></label>
                <input type="datetime-local" class="form-control" v-model="editScheduleForm.shift_end_time" />
              </div>
            </div>

            <div class="custom-control custom-switch mb-3">
              <input type="checkbox" class="custom-control-input" id="editIsOvertime"
                     v-model="editScheduleForm.is_overtime" />
              <label class="custom-control-label" for="editIsOvertime" style="font-size: 0.88rem;">
                Hora extra
              </label>
            </div>

            <template v-if="editRemainingSlot">
              <div class="alert alert-info mb-2" style="font-size: 0.85rem; padding: 8px 12px;">
                <i class="fas fa-info-circle mr-1"></i>
                Horário restante:
                <div class="mt-1">
                  <strong>{{ editRemainingSlot.startDate }} {{ editRemainingSlot.start }}</strong>
                  <span class="text-muted mx-1">até</span>
                  <strong>{{ editRemainingSlot.endDate }} {{ editRemainingSlot.end }}</strong>
                </div>
              </div>

              <div class="custom-control custom-switch mb-3">
                <input type="checkbox" class="custom-control-input" id="editCreatePlantao"
                       v-model="editScheduleForm.create_plantao" />
                <label class="custom-control-label" for="editCreatePlantao" style="font-size: 0.9rem;">
                  Criar plantão para o horário restante
                </label>
              </div>

              <transition name="accordion">
                <div v-if="editScheduleForm.create_plantao" class="accordion-body">
                  <div class="form-row">
                    <div class="form-group col-md-8">
                      <label><strong>Título do plantão</strong></label>
                      <input type="text" class="form-control" v-model="editScheduleForm.plantao_title"
                             placeholder="Ex: Plantão tarde" maxlength="255" />
                    </div>
                    <div class="form-group col-md-4">
                      <label><strong>Vagas</strong></label>
                      <input type="number" class="form-control" v-model.number="editScheduleForm.plantao_vacancies" min="1" max="99" />
                    </div>
                  </div>
                  <div v-if="availableRequirements.length" class="form-group mb-0">
                    <div @click="showEditRequirements = !showEditRequirements"
                         style="display: flex; align-items: center; justify-content: space-between;
                                background: #f0f4f8; border: 1px solid #d1d9e0; border-radius: 6px;
                                padding: 7px 12px; cursor: pointer; user-select: none;
                                transition: background 0.15s;">
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <i class="fas fa-tag" style="font-size: 0.78rem; color: #6c757d;"></i>
                        <span style="font-size: 0.85rem; font-weight: 600; color: #495057;">Requisitos do plantão</span>
                        <span style="font-size: 0.78rem; color: #6c757d;">(opcional)</span>
                        <span v-if="editScheduleForm.plantao_requirement_ids.length" class="badge badge-primary" style="font-size: 0.7rem;">
                          {{ editScheduleForm.plantao_requirement_ids.length }}
                        </span>
                      </div>
                      <i :class="showEditRequirements ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                         style="font-size: 0.75rem; color: #6c757d;"></i>
                    </div>
                    <transition name="accordion">
                      <div v-if="showEditRequirements" style="border: 1px solid #d1d9e0; border-top: none; border-radius: 0 0 6px 6px; padding: 8px; background: #fff;">
                        <div style="max-height: 100px; overflow-y: auto;">
                          <div v-for="req in availableRequirements" :key="req.id" class="custom-control custom-checkbox" style="line-height: 1.6;">
                            <input type="checkbox" class="custom-control-input"
                                   :id="`edit-req-${req.id}`"
                                   :value="req.id"
                                   v-model="editScheduleForm.plantao_requirement_ids" />
                            <label class="custom-control-label" :for="`edit-req-${req.id}`"
                                   style="font-size: 0.82rem;">{{ req.name }}</label>
                          </div>
                        </div>
                        <small class="text-muted" style="font-size: 0.78rem;">Sem requisitos = visível para todos</small>
                      </div>
                    </transition>
                  </div>
                </div>
              </transition>
              <transition name="accordion">
                <div v-if="!editScheduleForm.create_plantao" class="alert alert-warning mb-0" style="font-size: 0.85rem; padding: 8px 12px;">
                  <i class="fas fa-exclamation-triangle"></i>
                  O horário restante não gerará nenhum plantão.
                </div>
              </transition>
            </template>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showEditScheduleModal = false">Cancelar</button>
            <button type="button" class="btn btn-primary" :disabled="savingEditSchedule" @click="submitEditSchedule">
              <span v-if="savingEditSchedule"><i class="fa fa-spinner fa-spin"></i> Salvando...</span>
              <span v-else><i class="fas fa-save"></i> Salvar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: plantão pendente de aprovação -->
    <div v-if="showPendingDutyShiftModal && selectedSwapEvent" class="modal fade show" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog" role="document" style="max-width: 480px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Plantão Aguardando Aprovação</h5>
            <button type="button" class="close" @click="closePendingDutyShiftModal"><span>&times;</span></button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div class="alert alert-warning d-flex align-items-center" style="gap: 8px; padding: 8px 12px;">
              <i class="fas fa-hourglass-half"></i>
              <span>Sua solicitação está aguardando aprovação.</span>
            </div>

            <div class="row mt-3">
              <div class="col-12">
                <div class="form-group">
                  <label><strong>Plantão</strong></label>
                  <div class="form-control-plaintext">{{ selectedSwapEvent.extendedProps.duty_shift && selectedSwapEvent.extendedProps.duty_shift.title }}</div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Data</strong></label>
                  <div class="form-control-plaintext">{{ formatDate(selectedSwapEvent.extendedProps.duty_day) }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Horário</strong></label>
                  <div class="form-control-plaintext" v-if="selectedSwapEvent.extendedProps.duty_shift">
                    <div>
                      <span class="text-muted" style="font-size:0.82rem;">Início:</span>
                      {{ formatDutyShiftTime(selectedSwapEvent.extendedProps.duty_shift.start_datetime) }}
                    </div>
                    <div>
                      <span class="text-muted" style="font-size:0.82rem;">Fim:</span>
                      {{ formatDutyShiftTime(selectedSwapEvent.extendedProps.duty_shift.end_datetime) }}
                      <span
                        v-if="pendingShiftEndsNextDay"
                        class="badge badge-secondary ml-1"
                        style="font-size:0.7rem; vertical-align:middle;"
                      >dia seguinte</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePendingDutyShiftModal">Fechar</button>
            <button
              type="button"
              class="btn btn-danger ml-2"
              :disabled="cancelingShiftId === selectedSwapEvent.extendedProps.duty_shift_allocation_id"
              @click="cancelPendingDutyShift"
            >
              <span v-if="cancelingShiftId === selectedSwapEvent.extendedProps.duty_shift_allocation_id">
                <i class="fa fa-spinner fa-spin"></i> Cancelando...
              </span>
              <span v-else><i class="fas fa-times"></i> Cancelar solicitação</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: solicitar assumir outro plantão -->
    <div v-if="showRequestShiftModal" class="custom-modal-overlay">
      <div class="modal-dialog request-shift-modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Solicitar plantão</h5>
            <button type="button" class="close" @click="closeRequestShiftModal"><span>&times;</span></button>
          </div>
          <div class="modal-body">
            <p class="text-muted mb-3">Selecione um dia para ver os plantões disponíveis e solicite o desejado.</p>

            <div class="request-month-picker mb-3">
              <label class="mb-1" style="font-size: 0.85rem; color: #6c757d;">Mês de referência</label>
              <v-select
                v-model="requestShiftMonth"
                :options="months"
                label="label"
                :reduce="m => m.value"
                :clearable="false"
                :searchable="true"
                placeholder="Digite o mês"
                @input="onRequestMonthChange"
              />
            </div>

            <div class="border rounded p-2 mb-3 request-days-wrap">
              <div v-if="!requestMonthDays.length" class="text-muted small text-center py-2">
                Nenhum plantão disponível neste mês.
              </div>
              <div v-else class="request-days-grid">
                <button
                  v-for="day in requestMonthDays"
                  :key="day.dateKey"
                  type="button"
                  class="btn btn-sm request-day-btn"
                  :class="day.hasPendingRequest ? 'btn-outline-secondary disabled' : (requestSelectedDate === day.dateKey ? 'btn-primary' : 'btn-outline-secondary')"
                  :disabled="day.hasPendingRequest"
                  @click="selectRequestDate(day.dateKey)"
                >
                  {{ day.label }}
                </button>
              </div>
            </div>

            <div v-if="requestSelectedDate">
              <p class="text-muted small mb-2">Plantões disponíveis em {{ formatDate(requestSelectedDate) }}:</p>
              <div v-if="!requestDayDutyShifts.length" class="text-muted small py-2">
                Nenhum plantão disponível neste dia.
              </div>
              <div style="max-height: 260px; overflow-y: auto;">
                <div
                  v-for="ds in requestDayDutyShifts"
                  :key="ds.id"
                  class="border rounded mb-2"
                  style="padding: 10px 14px; cursor: pointer; transition: background 0.15s;"
                  :style="requestSelectedDutyShiftId === ds.id ? 'background:#e6f4ea; border-color:#28a745 !important;' : ''"
                  @click="requestSelectedDutyShiftId = ds.id"
                >
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <strong>{{ ds.title }}</strong>
                      <div class="text-muted small">
                        {{ formatDutyShiftDate(ds.start_datetime) }} — {{ formatDutyShiftTime(ds.end_datetime) }}
                        <span v-if="ds.department"> · {{ ds.department.title }}</span>
                      </div>
                      <div class="text-muted small">
                        {{ ds.available_vacancies }} vaga(s) disponível(is)
                      </div>
                    </div>
                    <i v-if="requestSelectedDutyShiftId === ds.id" class="fas fa-check-circle text-success fa-lg"></i>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small py-2">
              Selecione um dia acima para ver os plantões disponíveis.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeRequestShiftModal">Fechar</button>
            <button
              type="button"
              class="btn btn-add ml-2"
              :disabled="!requestSelectedDutyShiftId || swapping"
              @click="submitDutyShiftRequest"
            >
              <span v-if="swapping"><i class="fa fa-spinner fa-spin"></i> Enviando...</span>
              <span v-else>Solicitar <i class="fa fa-check"></i></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: solicitar liberação parcial (is_admin=3) -->
    <div v-if="showRequestReleaseModal && selectedSwapEvent" class="modal fade show" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0,0,0,0.5); z-index: 1060;">
      <div class="modal-dialog" role="document" style="max-width: 460px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fas fa-unlock mr-2"></i>Solicitar liberação</h5>
            <button type="button" class="close" @click="showRequestReleaseModal = false"><span>&times;</span></button>
          </div>
          <div class="modal-body" style="padding: 20px;">
            <div class="mb-3 p-2 bg-light rounded" style="font-size: 0.88rem;">
              <strong>{{ getDisplayEmployeeName() }}</strong> — {{ formatDate(selectedSwapEvent.start) }}
            </div>

            <div v-if="eventShiftTimes" class="mb-2" style="font-size: 0.85rem; color: #6c757d;">
              Horário original: <strong>{{ eventShiftTimes.startDate }} {{ eventShiftTimes.start }}</strong> – <strong>{{ eventShiftTimes.endDate }} {{ eventShiftTimes.end }}</strong>
            </div>

            <!-- Tipo de liberação -->
            <div class="form-group mb-2">
              <label style="font-size:0.85rem; margin-bottom:4px;"><strong>Tipo de liberação</strong></label>
              <div class="btn-group btn-group-sm d-flex">
                <button type="button" class="btn flex-fill"
                  :class="requestReleaseForm.release_type === 'full' ? 'btn-warning' : 'btn-outline-secondary'"
                  @click="requestReleaseForm.release_type = 'full'">
                  <i class="fas fa-unlock mr-1"></i> Turno completo
                </button>
                <button type="button" class="btn flex-fill"
                  :class="requestReleaseForm.release_type === 'partial' ? 'btn-warning' : 'btn-outline-secondary'"
                  @click="requestReleaseForm.release_type = 'partial'">
                  <i class="fas fa-cut mr-1"></i> Turno parcial
                </button>
              </div>
            </div>

            <!-- Horários — apenas quando parcial -->
            <template v-if="requestReleaseForm.release_type === 'partial'">
              <div v-if="eventShiftTimes" class="alert alert-secondary py-2 px-3 mb-2" style="font-size: 0.82rem;">
                <i class="fas fa-lock mr-1"></i>
                Você só pode ajustar o <strong>horário</strong> dentro da escala
                (<strong>{{ eventShiftTimes.start }}</strong>–<strong>{{ eventShiftTimes.end }}</strong>).
                A <strong>data</strong> não pode ser alterada.
              </div>
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label><strong>Novo início</strong></label>
                  <input type="time" class="form-control" v-model="requestReleaseForm.proposed_start_time"
                         :min="releaseTimeBounds.min" :max="releaseTimeBounds.max" />
                </div>
                <div class="form-group col-md-6">
                  <label><strong>Novo fim</strong></label>
                  <input type="time" class="form-control" v-model="requestReleaseForm.proposed_end_time"
                         :min="releaseTimeBounds.min" :max="releaseTimeBounds.max" />
                </div>
              </div>
              <div v-if="requestReleaseRemainingSlot" class="alert alert-info mb-3" style="font-size: 0.85rem; padding: 8px 12px;">
                <i class="fas fa-info-circle mr-1"></i>
                Horário que virará plantão (após aprovação):
                <div class="mt-1">
                  <strong>{{ requestReleaseRemainingSlot.startDate }} {{ requestReleaseRemainingSlot.start }}</strong>
                  <span class="text-muted mx-1">até</span>
                  <strong>{{ requestReleaseRemainingSlot.endDate }} {{ requestReleaseRemainingSlot.end }}</strong>
                </div>
              </div>
            </template>

            <template v-else>
              <div v-if="eventShiftTimes" class="card border-warning mb-3" style="font-size: 0.88rem;">
                <div class="card-body" style="padding: 10px 14px;">
                  <div class="text-muted mb-2" style="font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;">
                    Turno completo solicitado para liberação
                  </div>
                  <div class="d-flex" style="gap: 24px;">
                    <div>
                      <div class="text-muted" style="font-size: 0.75rem;">Início</div>
                      <strong>{{ eventShiftTimes.startDate }}</strong>
                      <span class="badge badge-secondary ml-1">{{ eventShiftTimes.start }}</span>
                    </div>
                    <div>
                      <div class="text-muted" style="font-size: 0.75rem;">Fim</div>
                      <strong>{{ eventShiftTimes.endDate }}</strong>
                      <span class="badge badge-secondary ml-1">{{ eventShiftTimes.end }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="form-group mb-0">
              <label><strong>Motivo</strong> <span class="text-danger">*</span></label>
              <textarea class="form-control" v-model="requestReleaseForm.reason" rows="3"
                        placeholder="Descreva o motivo da solicitação de liberação" maxlength="500"></textarea>
              <small class="text-muted">{{ (requestReleaseForm.reason || '').length }}/500</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showRequestReleaseModal = false">Cancelar</button>
            <button type="button" class="btn btn-warning" :disabled="submittingRequestRelease" @click="submitRequestRelease">
              <span v-if="submittingRequestRelease"><i class="fa fa-spinner fa-spin"></i> Enviando...</span>
              <span v-else><i class="fas fa-paper-plane"></i> Enviar solicitação</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isImportModalVisible && !isUserView" class="custom-modal-overlay">
      <div class="modal-dialog import-modal-dialog" role="document">
        <div class="modal-content import-modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Importar Escalas</h5>
            <button type="button" class="close" @click="closeImportModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <label style="font-size:0.82rem; color:#6c757d; margin-bottom:6px; display:block;">
                Formato aceito: <strong>.xlsx, .xls, .csv</strong>
              </label>
              <div
                style="position:relative; border:2px dashed #c5cdd6; border-radius:10px; background:#f8fafc; padding:28px 16px; text-align:center; cursor:pointer; transition: border-color .2s;"
              >
                <input ref="importFileInput" type="file" @change="onImportFileChange" @click.stop="preventMultipleClicks" accept=".xlsx,.xls,.csv"
                       style="position:absolute; inset:0; width:100%; height:100%; opacity:0; cursor:pointer; z-index:2;" />
                <i class="fas fa-cloud-upload-alt" style="font-size:36px; color:#90a0b7; display:block; margin-bottom:10px;"></i>
                <div style="font-size:1rem; font-weight:600; color:#3d4a5c; margin-bottom:4px;">
                  Clique ou arraste o arquivo aqui
                </div>
                <div style="font-size:0.84rem; color:#8a9bb0;">
                  {{ selectedImportFile ? '✔ ' + selectedImportFile.name : 'Nenhum arquivo selecionado' }}
                </div>
              </div>
            </div>

            <div class="form-group">
              <div class="custom-control custom-checkbox">
                <input
                  id="importReplaceExisting"
                  v-model="importReplaceExisting"
                  type="checkbox"
                  class="custom-control-input"
                  :disabled="isImportUploading"
                />
                <label class="custom-control-label" for="importReplaceExisting" style="font-weight:600; color:#3d4a5c;">
                  Substituir escalas existentes
                </label>
              </div>
              <small class="form-text text-muted">
                Dias que já têm escala e aparecem na planilha são refeitos com os dados do arquivo.
                Dias fora da planilha não são alterados. Escalas trocadas nunca são substituídas.
              </small>
            </div>

            <div v-if="isImportUploading" style="margin-top:12px;">
              <div v-if="importProgressPercent && importProgressPercent > 0" style="display:flex; align-items:center; gap:8px;">
                <div style="flex:1; background:#e9ecef; height:10px; border-radius:6px; overflow:hidden;">
                  <div :style="{width: importProgressPercent + '%', background: '#007bff', height: '100%'}"></div>
                </div>
                <div style="min-width:120px; font-size:0.85rem; color:#495057">{{ importStatus || (importProgressPercent + '%') }}</div>
              </div>
              <div v-else style="display:flex; align-items:center; gap:8px;">
                <i class="fa fa-spinner fa-spin" style="font-size:18px; color:#007bff;"></i>
                <div style="min-width:120px; font-size:0.95rem; color:#495057">{{ importStatus || 'Enviando arquivo...' }}</div>
              </div>
            </div>

          </div>

          <div class="modal-footer import-modal-footer">
            <div class="left-actions">
              <button class="btn btn-outline-primary btn-sm" :disabled="isImportUploading" @click="downloadTemplate">
                <i class="fas fa-download"></i>
                <span class="d-none d-sm-inline"> Baixar template</span>
              </button>
            </div>
            <div class="right-actions">
              <button class="btn btn-secondary btn-sm" @click="closeImportModal">Fechar</button>
              <button class="btn btn-primary btn-sm ml-2" :disabled="isImportUploading || !selectedImportFile" @click="uploadImport">
                <span v-if="isImportUploading" class="spinner-border spinner-border-sm mr-2"></span>
                Enviar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isImportErrorsModalVisible" class="custom-modal-overlay">
      <div class="modal-dialog import-modal-dialog" role="document">
        <div class="modal-content import-modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Por favor, valide estas linhas</h5>
            <button type="button" class="close" @click="closeImportErrorsModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <p style="margin-bottom:10px; color:#6c757d;">Abaixo estão as linhas da planilha que apresentam problemas. Corrija no arquivo e tente novamente.</p>

            <div class="import-errors">
              <ul style="padding-left: 18px; margin:0;">
                <li v-for="(err, idx) in (showAllErrorsExpanded ? importErrorList : importErrorList.slice(0,6))" :key="idx" style="margin-bottom:6px;">
                  <strong>Linha {{ err.line || (err.lines && err.lines[0]) || '?' }}:</strong>
                  <span style="margin-left:6px;">{{ err.error || err.value || JSON.stringify(err) }}</span>
                </li>
              </ul>
            </div>

            <div v-if="importErrorList && importErrorList.length > 6" style="margin-top:10px;">
              <button class="btn btn-link p-0" @click.prevent="showAllErrorsExpanded = !showAllErrorsExpanded">
                {{ showAllErrorsExpanded ? 'Ver menos' : `Ver mais (${importErrorList.length - 6} restantes)` }}
              </button>
            </div>

            <div style="margin-top:12px; color:#495057; font-size:0.95rem;">
              <strong>Sugestões rápidas:</strong>
              <ul>
                <li>Verifique CPF (ex: 000.000.000-00)</li>
                <li>Verifique COMPETENCIA (ex: 04/2026 ou abril/2026)</li>
                <li>Confirme se o colaborador existe na base</li>
              </ul>
            </div>
          </div>

          <div class="modal-footer import-modal-footer">
            <div class="left-actions"></div>
            <div class="right-actions">
              <button class="btn btn-secondary btn-sm" @click="closeImportErrorsModal">OK</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
 

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'

export default {
  name: 'WorkScheduleSystem',
  components: {FullCalendar},

  data() {
    return {
      swapPendingCount: 0,
      swapCancellationPendingCount: 0,
      loading: false,
      pageReady: false,
      saving: false,
      swapping: false,
      releasingSchedule: false,

      selectionMode: false,
      selectedScheduleIds: [],

      showScheduleModal: false,
      showEventModal: false,
      showMultipleEventsModal: false,
      showRequestShiftModal: false,
      showPendingDutyShiftModal: false,
      requestShiftMonth: new Date().toISOString().slice(0, 7),
      requestSelectedDate: '',
      requestSelectedDutyShiftId: null,

      isImportModalVisible: false,
      isImportUploading: false,
      importResult: null,
      importErrorList: [],
      selectedImportFile: null,

      importReplaceExisting: false,

      importProgressPercent: 0,
      importStatus: '',

      importJobId: null,
      isImportInBackground: false,

      isImportErrorsModalVisible: false,
      showAllErrorsExpanded: false,
      processedCount: 0,
      swapApprovalConfig: null,

      isQuickSchedule: false,
      quickScheduleDate: null,

      departments: [],
      employees: [],
      schedules: [],
      userWorktimes: {},

      loadingEmployees: false,
      employeeContextType: null,
      employeeContextDepartmentId: null,
      employeeCache: new Map(),
      scheduleCache: new Map(),
      scheduleCacheTtl: 180000,

      currentPeriodName: '',
      selectedMonth: new Date().toISOString().slice(0, 7),
      currentCalendarDate: new Date().toISOString().slice(0, 10),
      selectedDepartment: '',
      selectedEmployee: null,
      calendarView: 'dayGridMonth',

      multipleEventsModalTitle: '',
      multipleEventsData: [],
      multipleEventsSearch: '',

      newSchedule: {month: new Date().toISOString().slice(0, 7), employeeId: null, departmentId: '', workDays: [], isOvertime: false, dutyShiftId: null, shiftStartTime: '', shiftEndTime: ''},
      loadingModalWorktime: false,
      dutyShiftsOnSelectedDate: [],
      loadingDutyShifts: false,
      selectedSwapEvent: null,

      availableDutyShifts: [],
      loadingDutyShifts: false,
      requestingShiftId: null,
      cancelingShiftId: null,
      userAllocations: {},
      myDutyShiftAllocations: [],

      showReleaseModal: false,
      releaseSlots: [],
      releasingSchedule: false,
      approvingReleaseRequest: null,
      releaseRequests: {},
      pendingSwaps: {},

      showEditScheduleModal: false,
      showEditRequirements: false,
      editScheduleForm: { shift_start_time: '', shift_end_time: '', is_overtime: false, plantao_title: '', plantao_vacancies: 1, plantao_requirement_ids: [], create_plantao: true },
      savingEditSchedule: false,

      showRequestReleaseModal: false,
      requestReleaseForm: { release_type: 'full', proposed_start_time: '', proposed_end_time: '', reason: '' },
      submittingRequestRelease: false,

      approveReleaseForm: { plantao_title: '', plantao_vacancies: 1, plantao_requirement_ids: [], create_plantao: false },

      availableRequirements: [],
    }
  },


  computed: {
    isUserView() {
      const user = this.$store.getters.user
      return user && user.is_admin === 3
    },

    currentEventReleaseRequest() {
      if (!this.selectedSwapEvent) return null
      const scheduleId = this.selectedSwapEvent.extendedProps.id
      return this.releaseRequests[scheduleId] || null
    },

    selectedSingleDay() {
      if (this.isQuickSchedule) return this.quickScheduleDate
      if (this.newSchedule.workDays.length === 1) {
        return this.newSchedule.month + '-' + String(this.newSchedule.workDays[0]).padStart(2, '0')
      }
      return null
    },

    newScheduleShiftDates() {
      const day = this.selectedSingleDay
      const start = this.newSchedule.shiftStartTime
      const end   = this.newSchedule.shiftEndTime
      if (!day || !start) return null
      const startDate = this.formatDate(day)
      let endDate = startDate
      if (end && this.timeStrToMinutes(end) <= this.timeStrToMinutes(start)) {
        const d = new Date(day + 'T00:00:00')
        d.setDate(d.getDate() + 1)
        endDate = this.formatDate(d.toISOString().split('T')[0])
      }
      return { startDate, endDate }
    },

    importInBackground() {
      return this.isImportInBackground
    },

    isImportBlocked() {
      return this.isImportInBackground || this.isImportUploading
    },

    isSwapApprovalEnabled() {
      return !!(this.swapApprovalConfig && this.swapApprovalConfig.enable_schedule_swap_approval)
    },

    calendarOptions() {
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: this.calendarView,
        initialDate: this.currentCalendarDate,
        headerToolbar: false,
        locale: ptBrLocale,
        height: 'auto',
        events: this.calendarEvents,
        eventClick: this.onEventClick,
        dateClick: this.onDateClick,
        selectable: false,
        datesSet: this.onDatesSet,
        dayMaxEvents: 4,
        moreLinkClick: (info) => {
          info.jsEvent.preventDefault()
          info.jsEvent.stopPropagation()
          this.openMultipleEventsModal(info.date)
          return false
        },
        eventDisplay: 'block',
        displayEventTime: this.calendarView === 'timeGridWeek',
        eventOverlap: true,
        slotEventOverlap: true,
        eventMaxStack: 3
      }
    },

    calendarEvents() {
      const events = this.schedules.map((schedule, index) => {
        const isSwapped = schedule.is_swapped && schedule.swapped_user_id
        const isOvertime = !!schedule.is_overtime
        const userId = isSwapped ? schedule.swapped_user_id : schedule.user_id
        const joinedWorkingName = isSwapped ? schedule.swapped_user_name : schedule.user_name
        const joinedOriginalName = schedule.user_name
        const user = isSwapped
          ? (schedule.swapped_user || (joinedWorkingName ? {id: userId, name: joinedWorkingName} : this.getEmployeeById(userId)))
          : (schedule.user || (joinedWorkingName ? {id: userId, name: joinedWorkingName} : this.getEmployeeById(userId)))
        const originalUser = schedule.user || (joinedOriginalName ? {id: schedule.user_id, name: joinedOriginalName} : this.getEmployeeById(schedule.user_id))

        const isDutyShift = !!schedule.duty_shift_allocation_id
        const releaseRequest = this.releaseRequests[schedule.id] || null
        const hasPendingRelease = !!releaseRequest
        const pendingSwap = this.pendingSwaps[schedule.id] || null
        const hasPendingSwap = !!pendingSwap
        const isSelected = this.selectionMode && this.selectedScheduleIds.includes(schedule.id)
        const eventObj = {
          id: `${schedule.id}-${index}`,
          color: isDutyShift ? '#8e44ad' : (hasPendingRelease ? '#e67e22' : (hasPendingSwap ? '#17a2b8' : (isSwapped ? '#FF9800' : (isOvertime ? '#28a745' : '#007bff')))),
          borderColor: isSelected ? '#dc3545' : undefined,
          title: `${isSelected ? '✅ ' : ''}${user
            ? `${user.name}${isDutyShift ? ' 🏥' : ''}${hasPendingSwap ? ' ⏳ (Troca pendente)' : ''}${isSwapped ? ' (Trocado)' : ''}${isOvertime ? ' (HE)' : ''}${hasPendingRelease ? ' ⚠️' : ''}`
            : `ID: ${userId}${hasPendingSwap ? ' ⏳ (Troca pendente)' : ''}${hasPendingRelease ? ' ⚠️' : ''}`}`,
          extendedProps: {...schedule, workingUser: user, originalUser, isDutyShift, release_request: releaseRequest, pending_swap: pendingSwap},
          allDay: this.calendarView !== 'timeGridWeek'
        }

        if (this.calendarView === 'timeGridWeek' && this.userWorktimes[userId]) {
          const worktime = this.userWorktimes[userId]
          const date = schedule.duty_day.split('T')[0]
          eventObj.start = `${date}T${worktime.start_time}`
          eventObj.end = `${date}T${worktime.end_time}`
          eventObj.allDay = false
        } else {
          eventObj.start = schedule.duty_day.split('T')[0]
          eventObj.allDay = true
        }
        return eventObj
      })

      ;(this.myDutyShiftAllocations || []).forEach(alloc => {
        if (alloc.status !== 'pending' || !alloc.duty_shift) return
        const ds = alloc.duty_shift
        const dateKey = ds.start_datetime ? ds.start_datetime.split('T')[0] : null
        if (!dateKey) return
        events.push({
          id: `duty-shift-pending-${alloc.id}`,
          color: '#e67e22',
          title: `⏳ ${ds.title} (Aguardando)`,
          start: dateKey,
          allDay: true,
          extendedProps: {
            is_duty_shift_pending: true,
            duty_shift_allocation_id: alloc.id,
            duty_shift: ds,
            duty_day: dateKey,
          },
        })
      })

      return events
    },

    formattedPeriod() {
      const [year, month] = this.selectedMonth.split('-')
      return new Date(year, month - 1).toLocaleString('pt-BR', {month: 'long', year: 'numeric'})
    },

    months() {
      const monthsList = [], now = new Date()
      for (let i = -6; i <= 6; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() + i, 1)
        const value = date.toISOString().slice(0, 7)
        const label = date.toLocaleString('pt-BR', {month: 'long', year: 'numeric'})
        if (!monthsList.find(m => m.value === value)) {
          monthsList.push({value, label})
        }
      }
      return monthsList
    },

    monthDays() {
      const [year, month] = this.newSchedule.month.split('-')
      const daysInMonth = new Date(year, month, 0).getDate()
      return Array.from({length: daysInMonth}, (_, i) => i + 1)
    },

    filteredEmployees() {
      if (!this.selectedDepartment) return []
      return this.employees.filter(emp => emp.department_id == this.selectedDepartment)
    },

    modalEmployees() {
      if (!this.newSchedule.departmentId) return []
      return this.employees.filter(emp => emp.department_id == this.newSchedule.departmentId)
    },

    requestMonthDays() {
      const [year, month] = this.getRequestMonthParts()
      const dutyShiftDates = new Set(
        (this.availableDutyShifts || []).map(s => {
          const d = new Date(s.start_datetime)
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        })
      )
      const currentUserId = this.$store.getters.user && this.$store.getters.user.id
      const days = []
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      for (let day = 1; day <= daysInMonth; day++) {
        const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        if (!dutyShiftDates.has(dateKey)) continue
        const alreadyAllocated = (this.availableDutyShifts || []).some(s => {
          const d = new Date(s.start_datetime)
          const dk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
          return dk === dateKey && (s.user_status === 'approved' || s.user_status === 'pending')
        })
        days.push({
          dateKey,
          label: String(day).padStart(2, '0'),
          hasOwnSchedule: false,
          hasPendingRequest: alreadyAllocated,
        })
      }
      return days
    },

    requestDayDutyShifts() {
      if (!this.requestSelectedDate) return []
      return (this.availableDutyShifts || []).filter(s => {
        const d = new Date(s.start_datetime)
        const dk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        return dk === this.requestSelectedDate
      })
    },

    userWorktime() {
      if (!this.selectedSwapEvent) return null
      const userId = this.selectedSwapEvent.extendedProps.is_swapped ?
          this.selectedSwapEvent.extendedProps.swapped_user_id :
          this.selectedSwapEvent.extendedProps.user_id
      return this.userWorktimes[userId]
    },

    eventShiftTimes() {
      if (!this.selectedSwapEvent) return null
      const ext = this.selectedSwapEvent.extendedProps
      const start = ext.shift_start_time ? this.extractTime(ext.shift_start_time) : (this.userWorktime && this.userWorktime.start_time) || null
      const end   = ext.shift_end_time   ? this.extractTime(ext.shift_end_time)   : (this.userWorktime && this.userWorktime.end_time)   || null
      if (!start || !end) return null

      const startMin = this.timeStrToMinutes(start)
      const endMin   = this.timeStrToMinutes(end)
      const nextDay  = endMin <= startMin

      const dutyDay = this.getEventDateKey(this.selectedSwapEvent.start)
      const startDate = this.formatDate(dutyDay)
      let endDate = startDate
      if (nextDay && dutyDay) {
        const d = new Date(dutyDay + 'T00:00:00')
        d.setDate(d.getDate() + 1)
        endDate = this.formatDate(this.getEventDateKey(d))
      }

      return { start, end, startDate, endDate, nextDay }
    },

    filteredMultipleEvents() {
      if (!this.multipleEventsSearch) {
        return this.multipleEventsData
      }

      const search = this.multipleEventsSearch.toLowerCase()

      return this.multipleEventsData.filter(event => {
        const employeeName = this.getEmployeeNameFromEvent(event).toLowerCase()
        const departmentName = this.getDepartmentNameFromEventData(event).toLowerCase()
        return employeeName.includes(search) || departmentName.includes(search)
      })
    },

    pendingShiftEndsNextDay() {
      if (!this.selectedSwapEvent) return false
      const ds = this.selectedSwapEvent.extendedProps.duty_shift
      if (!ds || !ds.start_datetime || !ds.end_datetime) return false
      const startH = new Date(ds.start_datetime).getHours() * 60 + new Date(ds.start_datetime).getMinutes()
      const endH   = new Date(ds.end_datetime).getHours()   * 60 + new Date(ds.end_datetime).getMinutes()
      return endH <= startH
    },

    editRemainingSlot() {
      const ext = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps
      if (!ext || !ext.shift_start_time || !ext.shift_end_time) return null
      const newStartStr = this.editScheduleForm.shift_start_time
      const newEndStr   = this.editScheduleForm.shift_end_time
      if (!newStartStr || !newEndStr) return null

      const dutyDay = this.getEventDateKey(this.selectedSwapEvent.start)

      const toFullDt = (val, refDayStr, refTimeStr) => {
        const str = String(val)
        if (str.includes(' ') && str.length > 10) return str.replace(' ', 'T').slice(0, 16)
        if (str.includes('T') && str.length > 10) return str.slice(0, 16)
        const time = str.slice(0, 5)
        if (refTimeStr) {
          if (this.timeStrToMinutes(time) <= this.timeStrToMinutes(refTimeStr)) {
            const d = new Date(refDayStr + 'T00:00:00'); d.setDate(d.getDate() + 1)
            return d.toISOString().split('T')[0] + 'T' + time
          }
        }
        return refDayStr + 'T' + time
      }

      const oldStartFull = toFullDt(ext.shift_start_time, dutyDay, null)
      const oldEndFull   = toFullDt(ext.shift_end_time, dutyDay, oldStartFull.slice(11, 16))

      const oStart = new Date(oldStartFull).getTime()
      const oEnd   = new Date(oldEndFull).getTime()
      const nStart = new Date(newStartStr).getTime()
      const nEnd   = new Date(newEndStr).getTime()

      if (isNaN(oStart) || isNaN(oEnd) || isNaN(nStart) || isNaN(nEnd)) return null
      if (oEnd <= oStart || nEnd <= nStart) return null
      if ((nEnd - nStart) >= (oEnd - oStart)) return null
      if (nEnd >= oEnd) return null

      const fmt = (ms) => {
        const d = new Date(ms)
        const pad = n => String(n).padStart(2, '0')
        const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        return { date: this.formatDate(dateStr), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }
      }
      const rStart = fmt(nEnd)
      const rEnd   = fmt(oEnd)
      return { start: rStart.time, end: rEnd.time, startDate: rStart.date, endDate: rEnd.date }
    },

    releaseTimeBounds() {
      const times = this.eventShiftTimes
      if (!times || times.nextDay) return { min: null, max: null }
      return { min: times.start, max: times.end }
    },

    requestReleaseRemainingSlot() {
      const times = this.eventShiftTimes
      if (!times) return null
      const { proposed_start_time: newStartRaw, proposed_end_time: newEndRaw } = this.requestReleaseForm
      if (!newStartRaw || !newEndRaw) return null
      const newStart = newStartRaw.includes('T') ? newStartRaw.slice(11, 16) : newStartRaw.slice(0, 5)
      const newEnd   = newEndRaw.includes('T')   ? newEndRaw.slice(11, 16)   : newEndRaw.slice(0, 5)
      const dutyDay = this.selectedSwapEvent ? this.getEventDateKey(this.selectedSwapEvent.start) : null
      return this.calcRemainingSlot(times.start, times.end, newStart, newEnd, dutyDay)
    },

    approveModalRemainingSlot() {
      const rr = this.approvingReleaseRequest
      if (!rr || !rr.proposed_start_time || !rr.proposed_end_time) return null
      const times = this.eventShiftTimes
      if (!times) return null
      const dutyDay = this.selectedSwapEvent ? this.getEventDateKey(this.selectedSwapEvent.start) : null
      return this.calcRemainingSlot(times.start, times.end, rr.proposed_start_time, rr.proposed_end_time, dutyDay)
    },
  },

  watch: {
    async calendarView() {
      this.clearScheduleCache()
      await this.loadSchedules()
    },

    selectedDepartment() {
      this.resetEmployeeResults()
    },

    'newSchedule.departmentId'() {
      this.resetEmployeeResults()
    },

    async 'newSchedule.employeeId'(newId) {
      this.newSchedule.shiftStartTime = ''
      this.newSchedule.shiftEndTime   = ''
      if (!newId) return
      this.loadingModalWorktime = true
      try {
        const { data } = await axios.get(`${this.$store.getters.api}users_worktimes?user_id=${newId}`)
        const list     = data.data || []
        const wt       = list[0] && list[0].worktime
        const start    = wt && (wt.expected_entry_time || wt.start_time)
        const end      = wt && (wt.expected_exit_time  || wt.end_time)
        this.newSchedule.shiftStartTime = start ? String(start).slice(0, 5) : ''
        this.newSchedule.shiftEndTime   = end   ? String(end).slice(0, 5)   : ''
        this.$set(this.userWorktimes, newId, { start_time: start || null, end_time: end || null })
      } catch (e) {
        console.error('Erro ao carregar jornada:', e)
      } finally {
        this.loadingModalWorktime = false
      }
    },
  },

  async mounted() {
    this.scheduleCache.clear()
    await this.loadSwapApprovalConfiguration()
    await this.loadDepartments()
    await this.loadSchedules()
    this.updatePeriodName()
    this.pageReady = true
    this.loadAvailableDutyShifts()
    this.loadSwapPendingCount()
    this.loadSwapCancellationPendingCount()
    if (!this.isUserView) this.loadRequirements()
    if (this.isUserView) this.loadMyDutyShiftAllocations()
    try {
      const persistedJobId = localStorage.getItem('work_schedules_import_job_id')
      if (persistedJobId) {
        this.importJobId = persistedJobId
        this.isImportInBackground = true
        this.importStatus = 'Importação em andamento (em segundo plano)'
        this.startImportPolling(persistedJobId)
      }
    } catch (e) {
      console.error(e)
    }
  },

  methods: {
    selectImportFile() {
      if (this.selectingImportFile) return
      this.selectingImportFile = true
      this.$nextTick(() => {
        const input = this.$refs.importFileInput
        if (input) input.click()
        setTimeout(() => { this.selectingImportFile = false }, 300)
      })
    },

    preventMultipleClicks(e) {
      e.stopPropagation()
    },

    async loadDepartments() {
      try {
        const companyId = this.$store.getters.user && this.$store.getters.user.company_id
        const url = `${this.$store.getters.api}departments/${companyId ? `?company_id=${companyId}` : ''}`
        const {data} = await axios.get(url)
        this.departments = data.data || data || []
      } catch (error) {
        console.error(error)
        this.showError('Erro ao carregar departamentos')
      }
    },

    async loadSwapApprovalConfiguration() {
      try {
        const { data } = await axios.get(`${this.$store.getters.api}configurations`)
        const configs = (data && data.data) ? data.data : []
        this.swapApprovalConfig = configs[0] || { enable_schedule_swap_approval: false }
        console.log('[SwapApproval] API response:', data)
        console.log('[SwapApproval] configs array:', configs)
        console.log('[SwapApproval] swapApprovalConfig set to:', this.swapApprovalConfig)
        console.log('[SwapApproval] enable_schedule_swap_approval =', this.swapApprovalConfig.enable_schedule_swap_approval)
        console.log('[SwapApproval] isSwapApprovalEnabled =', !!(this.swapApprovalConfig && this.swapApprovalConfig.enable_schedule_swap_approval))
      } catch (error) {
        console.error('[SwapApproval] Erro ao carregar config:', error)
        this.swapApprovalConfig = { enable_schedule_swap_approval: false }
      }
    },

    clearScheduleCache() {
      this.scheduleCache.clear()
    },

    resetEmployeeResults() {
      this.employees = []
      this.employeeContextType = null
      this.employeeContextDepartmentId = null
    },

    getEmployeeCacheKey({departmentId}) {
      return String(departmentId || 'all')
    },

    applyEmployeesResponse(payload) {
      this.employees = payload.data || []
    },

    async fetchEmployees({departmentId, context}) {
      const cacheKey = this.getEmployeeCacheKey({departmentId: departmentId || 'all'})
      this.employeeContextDepartmentId = departmentId
      this.employeeContextType = context

      if (this.employeeCache.has(cacheKey)) {
        this.applyEmployeesResponse(this.employeeCache.get(cacheKey))
        return
      }

      this.loadingEmployees = true

      try {
        const companyId = this.$store.getters.user && this.$store.getters.user.company_id
        const params = new URLSearchParams()
        if (companyId) params.append('company_id', companyId)
        if (departmentId) params.append('department_id', departmentId)
        const url = `${this.$store.getters.api}users/select-options?${params.toString()}`
        const {data} = await axios.get(url)
        this.employeeCache.set(cacheKey, data)
        this.applyEmployeesResponse(data)
      } catch (error) {
        console.error(error)
        this.showError('Erro ao carregar funcionários')
      } finally {
        this.loadingEmployees = false
      }
    },

    onFilterEmployeeOpen() {
      this.fetchEmployees({departmentId: this.selectedDepartment, context: 'filter'})
    },

    onModalEmployeeOpen() {
      this.fetchEmployees({departmentId: this.newSchedule.departmentId, context: 'modal'})
    },


    getScheduleCacheKey() {
      return JSON.stringify({
        selectedMonth: this.selectedMonth,
        selectedDepartment: this.selectedDepartment,
        selectedEmployee: this.selectedEmployee,
        calendarView: this.calendarView,
        userId: this.isUserView ? (this.$store.getters.user && this.$store.getters.user.id) : null,
      })
    },

    async loadSchedules() {
      try {
        const cacheKey = this.getScheduleCacheKey()
        const cached = this.scheduleCache.get(cacheKey)

        if (cached && (Date.now() - cached.timestamp) < this.scheduleCacheTtl) {
          this.schedules = cached.data
          if (this.calendarView === 'timeGridWeek') {
            await this.loadUserWorktimes()
          }
          return
        }

        this.loading = true
        const params = new URLSearchParams()
        const companyId = this.$store.getters.user && this.$store.getters.user.company_id
        if (companyId) params.append('company_id', companyId)

        const [year, month] = this.selectedMonth.split('-')
        params.append('year', year)
        params.append('month', month)
        params.append('paginated', 'false')

        if (this.calendarView === 'dayGridMonth') {
          params.append('include_user_names', 'true')
        }

        if (this.isUserView) {
          const userId = this.$store.getters.user && this.$store.getters.user.id
          if (userId) params.append('user_id', userId)
        } else {
          if (this.selectedDepartment) params.append('department_id', this.selectedDepartment)
          if (this.selectedEmployee) params.append('user_id', this.selectedEmployee)
        }

        const url = `${this.$store.getters.api}work_schedules/?${params}`

        const {data} = await axios.get(url)
        this.schedules = data.data || data || []
        this.scheduleCache.set(cacheKey, {
          data: this.schedules,
          timestamp: Date.now(),
        })

        this._deriveReleaseRequestsFromSchedules()
        await this.loadPendingSwaps()

        if (this.calendarView === 'timeGridWeek') {
          await this.loadUserWorktimes()
        } else {
          this.userWorktimes = {}
        }
      } catch (error) {
        console.error('Error loading schedules:', error)
        this.showError('Erro ao carregar escalas')
      } finally {
        this.loading = false
      }
    },

    async loadUserWorktimes() {
      const userIds = [...new Set(this.schedules.map(s => s.is_swapped && s.swapped_user_id ? s.swapped_user_id : s.user_id).filter(Boolean))]
      if (!userIds.length) return
      try {
        const qs = userIds.map(id => `user_ids[]=${id}`).join('&')
        const { data } = await axios.get(`${this.$store.getters.api}users_worktimes?${qs}`)
        const map = data.data || {}
        Object.entries(map).forEach(([uid, times]) => {
          this.$set(this.userWorktimes, parseInt(uid), {
            start_time: times.start_time || '08:00',
            end_time:   times.end_time   || '17:00',
          })
        })
      } catch (e) {
        console.error('Error loading batch worktimes:', e)
      }
    },

    async loadWorktimeForUser(userId, fallback = false) {
      if (!userId || this.userWorktimes[userId]) return
      try {
        const {data} = await axios.get(`${this.$store.getters.api}users_worktimes?user_id=${userId}`)
        const worktimes = data.data || []
        const worktime = worktimes[0] && worktimes[0].worktime
        this.$set(this.userWorktimes, userId, {
          start_time: (worktime && (worktime.expected_entry_time || worktime.start_time)) || (fallback ? '08:00' : null),
          end_time:   (worktime && (worktime.expected_exit_time  || worktime.end_time))   || (fallback ? '17:00' : null),
        })
      } catch (error) {
        console.error(error)
        this.$set(this.userWorktimes, userId, {
          start_time: fallback ? '08:00' : null,
          end_time:   fallback ? '17:00' : null,
        })
      }
    },

    timeStrToMinutes(timeStr) {
      if (!timeStr) return 0
      const parts = timeStr.split(':')
      return Number(parts[0]) * 60 + Number(parts[1] || 0)
    },

    async saveSchedule() {
      this.saving = true
      try {
        if (!this.validateForm()) return
        const {conflicts, validDays} = this.getConflicts()

        if (conflicts.length > 0) {
          const choice = await this.confirmConflicts(conflicts, validDays)
          if (choice === 'cancel') return
          if (choice === 'replace') await this.deleteConflictingSchedules(conflicts)
        }

        await this.processDays(validDays, conflicts.length > 0)
        this.closeScheduleModal()
        this.clearScheduleCache()
        await this.loadSchedules()
        this.showSuccess('Escalas processadas com sucesso!')
      } catch (error) {
        console.error(error)
        this.showError(error.message || 'Erro ao salvar escalas')
      } finally {
        this.saving = false
      }
    },

    validateForm() {
      if (!this.newSchedule.employeeId) {
        this.showError('Selecione um colaborador')
        return false
      }
      if (!this.newSchedule.departmentId) {
        this.showError('Selecione um departamento')
        return false
      }
      if (!this.newSchedule.workDays.length) {
        this.showError('Selecione pelo menos um dia')
        return false
      }
      return true
    },

    getConflicts() {
      const conflicts = [], validDays = []
      const employeeId = parseInt(this.newSchedule.employeeId)

      this.newSchedule.workDays.forEach(day => {
        const dutyDay = this.isQuickSchedule ?
            this.quickScheduleDate :
            `${this.newSchedule.month}-${String(day).padStart(2, '0')}`

        const hasConflict = this.calendarEvents.some(event => {
          const props = event.extendedProps || {}
          const effectiveUserId = (props.is_swapped && props.swapped_user_id)
            ? parseInt(props.swapped_user_id)
            : parseInt(props.user_id)

          return event.start === dutyDay && effectiveUserId === employeeId
        })

        hasConflict ?
            conflicts.push({day, dutyDay, date: new Date(dutyDay).toLocaleDateString('pt-BR')}) :
            validDays.push({day, dutyDay})
      })

      return {conflicts, validDays}
    },

    async confirmConflicts(conflicts, validDays) {
      const message = `Conflitos encontrados em: ${conflicts.map(c => c.date).join(', ')}\nDias disponíveis: ${validDays.map(v => new Date(v.dutyDay).toLocaleDateString('pt-BR')).join(', ')}\n\nO que fazer?`

      return typeof swal !== 'undefined' ?
          new Promise(resolve => swal({
            title: 'Conflitos Encontrados', text: message, icon: 'warning',
            buttons: {
              cancel: {text: 'Cancelar', value: 'cancel'},
              onlyNew: validDays.length ? {text: `Criar só ${validDays.length} disponíveis`, value: 'new'} : null,
              replace: {text: `Substituir ${conflicts.length} existentes`, value: 'replace'}
            }
          }).then(resolve)) :
          confirm(`${message}\n1-Apenas novos 2-Substituir existentes`) ? 'replace' : 'cancel'
    },

    async deleteConflictingSchedules(conflicts) {
      const employeeId = parseInt(this.newSchedule.employeeId)

      const promises = conflicts.map(async conflict => {
        const event = this.calendarEvents.find(e => {
          const props = e.extendedProps || {}
          const effectiveUserId = (props.is_swapped && props.swapped_user_id)
            ? parseInt(props.swapped_user_id)
            : parseInt(props.user_id)

          return e.start === conflict.dutyDay && effectiveUserId === employeeId
        })

        return event ? axios.delete(`${this.$store.getters.api}work_schedules/${event.extendedProps.id}`) : null
      }).filter(Boolean)

      await Promise.all(promises)
    },

    async processDays(validDays, includeConflicts) {
      const companyId = this.$store.getters.user && this.$store.getters.user.company_id
      const daysToProcess = includeConflicts ?
          [...validDays, ...this.getConflicts().conflicts.map(c => ({day: c.day, dutyDay: c.dutyDay}))] :
          validDays

      const linkDutyShift = this.newSchedule.dutyShiftId && daysToProcess.length === 1
      const promises = daysToProcess.map(({dutyDay}) =>
          axios.post(`${this.$store.getters.api}work_schedules/`, {
            user_id: parseInt(this.newSchedule.employeeId),
            department_id: parseInt(this.newSchedule.departmentId),
            is_overtime: !!this.newSchedule.isOvertime,
            ...(this.newSchedule.shiftStartTime ? { shift_start_time: this.newSchedule.shiftStartTime } : {}),
            ...(this.newSchedule.shiftEndTime   ? { shift_end_time:   this.newSchedule.shiftEndTime   } : {}),
            duty_day: dutyDay,
            company_id: companyId,
            created_by: (this.$store.getters.user && this.$store.getters.user.id) || 1,
            ...(linkDutyShift ? {duty_shift_id: this.newSchedule.dutyShiftId} : {})
          }).catch((e) => { console.error(e); return null })
      )

      await Promise.all(promises)
    },

    onEventClick(info) {
      info.jsEvent.stopPropagation()
      const ext = info.event && info.event.extendedProps
      if (this.selectionMode) {
        if (ext && ext.is_duty_shift_pending) return
        const id = ext && ext.id
        if (id) this.toggleScheduleSelection(id)
        return
      }
      console.log('[EventClick] swapApprovalConfig:', this.swapApprovalConfig)
      console.log('[EventClick] isSwapApprovalEnabled:', this.isSwapApprovalEnabled)
      console.log('[EventClick] isUserView:', this.isUserView)
      console.log('[EventClick] isDutyShift:', ext && ext.isDutyShift)
      if (ext && ext.is_duty_shift_pending) {
        this.selectedSwapEvent = info.event
        this.showPendingDutyShiftModal = true
        return
      }
      this.selectedSwapEvent = info.event
      this.showEventModal = true
      if (ext && !ext.shift_start_time) {
        const userId = ext.is_swapped ? ext.swapped_user_id : ext.user_id
        if (userId && !this.userWorktimes[userId]) {
          this.loadWorktimeForUser(userId)
        }
      }
    },

    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode
      if (!this.selectionMode) this.selectedScheduleIds = []
    },

    toggleScheduleSelection(id) {
      const i = this.selectedScheduleIds.indexOf(id)
      if (i === -1) this.selectedScheduleIds.push(id)
      else this.selectedScheduleIds.splice(i, 1)
    },

    async bulkDeleteSelected() {
      if (!this.selectedScheduleIds.length) return
      const n = this.selectedScheduleIds.length
      if (!await this.confirm(`Excluir ${n} escala(s) selecionada(s)? Esta ação não pode ser desfeita.`)) return
      try {
        await axios.post(`${this.$store.getters.api}work_schedules/bulk-delete`, { ids: this.selectedScheduleIds })
        this.showSuccess(`${n} escala(s) excluída(s) com sucesso.`)
        this.selectionMode = false
        this.selectedScheduleIds = []
        if (this.scheduleCache && this.scheduleCache.clear) this.scheduleCache.clear()
        this.loadSchedules()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || (e.response && e.response.data) || 'Erro ao excluir escalas.'
        this.showError(msg)
      }
    },

    onMoreLinkClick(info) {
      this.openMultipleEventsModal(info.date)
      return false
    },

    onDateClick(info) {
      if (this.isUserView) return

      const year = info.date.getFullYear()
      const month = String(info.date.getMonth() + 1).padStart(2, '0')
      const day = String(info.date.getDate()).padStart(2, '0')

      this.openQuickSchedule(`${year}-${month}-${day}`)
    },

    onDatesSet(dateInfo) {
      this.updatePeriodName(dateInfo)

      const calendarApi = this.$refs.fullCalendar.getApi()
      // Preserva a data navegada para o resetOptions do FullCalendar (disparado
      // quando os eventos mudam) não voltar para "hoje".
      this.currentCalendarDate = calendarApi.getDate().toISOString().slice(0, 10)
      const actualMonth = this.currentCalendarDate.slice(0, 7)

      if (actualMonth !== this.selectedMonth) {
        this.selectedMonth = actualMonth
        this.loadSchedules()
      }
    },

    updatePeriodName(dateInfo = null) {
      if (!dateInfo) {
        this.currentPeriodName = this.formattedPeriod
        return
      }

      const calendarApi = this.$refs.fullCalendar.getApi()
      const currentViewDate = calendarApi.getDate()

      if (this.calendarView === 'dayGridMonth') {
        this.currentPeriodName = currentViewDate.toLocaleString('pt-BR', {month: 'long', year: 'numeric'})
      } else {
        const start = dateInfo.start
        const end = new Date(dateInfo.end.getTime() - 86400000)
        const startStr = start.toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'})
        const endStr = end.toLocaleDateString('pt-BR', {day: '2-digit', month: 'short', year: 'numeric'})
        this.currentPeriodName = `${startStr} - ${endStr}`
      }
    },

    onMonthChange() {
      this.navigateCalendar()
      this.loadSchedules()
    },

    onDepartmentChange() {
      this.selectedEmployee = null
      this.resetEmployeeResults()
      this.filterSchedules()
    },

    onModalDepartmentChange() {
      this.newSchedule.employeeId = null
      this.resetEmployeeResults()
    },

    async loadSwapPendingCount() {
      try {
        const { data } = await axios.get(`${this.$store.getters.api}work_schedule_swap_requests`)
        const list = (data && data.data) || []
        const myId = this.$store.getters.user && this.$store.getters.user.id
        let count = 0
        const user = this.$store.getters.user
        const canSelfApprove = user && [1, 2].includes(Number(user.is_admin))
        const scheduleMissing = r => (r.work_schedule_id && !r.work_schedule)
          || (r.target_work_schedule_id && !r.target_work_schedule)
        list.forEach(r => {
          if (this.isUserView) {
            if (Number(r.target_user_id) === Number(myId) && r.status === 'pending_peer') count++
          } else if (r.status === 'pending_approval'
            && (canSelfApprove || Number(r.requested_by_user_id) !== Number(myId))
            && !scheduleMissing(r)) {
            count++
          }
        })
        this.swapPendingCount = count
      } catch (e) {
        this.swapPendingCount = 0
      }
    },

    async loadSwapCancellationPendingCount() {
      try {
        const { data } = await axios.get(`${this.$store.getters.api}work_schedule_swap_cancellation_requests`)
        const raw = data && data.data
        const list = Array.isArray(raw) ? raw : ((raw && raw.data) || [])
        const myId = this.$store.getters.user && this.$store.getters.user.id
        let count = 0
        const user = this.$store.getters.user
        const canSelfApprove = user && [1, 2].includes(Number(user.is_admin))
        list.forEach(r => {
          if (this.isUserView) {
            if (Number(r.target_user_id) === Number(myId) && r.status === 'pending_peer') count++
          } else if (r.status === 'pending_approval'
            && (canSelfApprove || Number(r.requested_by_user_id) !== Number(myId))) {
            count++
          }
        })
        this.swapCancellationPendingCount = count
      } catch (e) {
        this.swapCancellationPendingCount = 0
      }
    },

    openScheduleModal() {
      if (this.isUserView) return
      this.resetScheduleForm()
      this.resetEmployeeResults()
      this.isQuickSchedule = false
      this.showScheduleModal = true
    },

    openImportModal() {
      if (this.isUserView) return
      if (this.isImportBlocked) {
        this.showWarning('Ja existe uma importacao em andamento. Aguarde a conclusao para iniciar outra.')
        return
      }
      this.importResult = null
      this.importErrorList = []
      this.selectedImportFile = null
      this.importReplaceExisting = false
      this.isImportModalVisible = true
    },

    closeImportModal() {
      this.isImportModalVisible = false
      this.importResult = null
      this.importErrorList = []
      this.selectedImportFile = null
      this.importReplaceExisting = false
      this.selectingImportFile = false
      this.resetImportFileInput()
    },

    resetImportFileInput() {
      const input = this.$refs.importFileInput
      if (input) {
        input.value = null
      }
    },

    onImportFileChange(e) {
      const f = e.target.files && e.target.files[0]
      this.selectedImportFile = f || null
    },

    showImportQueuedMessage() {
      const msg = 'Importacao iniciada. O processamento continua em segundo plano e o calendario sera atualizado automaticamente ao finalizar.'
      if (typeof swal !== 'undefined') {
        swal('Importacao em andamento', msg, 'info')
        return
      }

      alert(msg)
    },

    openImportErrorsModal(errors, processed = 0) {
      this.importErrorList = Array.isArray(errors) ? errors : [errors]
      this.processedCount = processed || 0
      this.showAllErrorsExpanded = false
      this.isImportErrorsModalVisible = true
    },

    closeImportErrorsModal() {
      this.isImportErrorsModalVisible = false
      this.importErrorList = []
      this.processedCount = 0
      this.showAllErrorsExpanded = false
    },

    async uploadImport() {
      if (!this.selectedImportFile) {
        this.showError('Selecione um arquivo')
        return
      }

      if (this.isImportInBackground) {
        this.showWarning('Ja existe uma importacao em andamento. Aguarde a conclusao para enviar novo arquivo.')
        return
      }

      this.isImportUploading = true
      this.importProgressPercent = 0
      this.importStatus = 'Enviando arquivo...'
      try {
        const fd = new FormData()
        fd.append('file', this.selectedImportFile)
        fd.append('replace_existing', this.importReplaceExisting ? '1' : '0')

        const url = `${this.$store.getters.api}work_schedules/import`
        const { data } = await axios.post(url, fd)

        if (data && data.job_id) {
          try { localStorage.setItem('work_schedules_import_job_id', data.job_id) } catch (e) { console.error(e) }
          this.importJobId = data.job_id
          this.isImportInBackground = true
          this.importStatus = 'Importação em andamento (em segundo plano)'
          this.showImportQueuedMessage()
          this.closeImportModal()
          this.startImportPolling(data.job_id)
        }
        else if (data && (data.reports !== undefined || data.errors !== undefined)) {
          const reports = data.reports || data
          const directErrors = data.errors || []
          this.importResult = reports
          this.importErrorList = directErrors

          const processed = Array.isArray(reports) ? reports.length : 0
          if (directErrors.length) {
            this.openImportErrorsModal(directErrors, processed)
          } else {
            this.showSuccess(`Importacao concluida com sucesso. ${processed} registro(s) processado(s).`)
          }

          this.importProgressPercent = 100
          this.importStatus = 'Concluído'
          this.isImportUploading = false
          this.clearScheduleCache()
          await this.loadSchedules()
        }
        else {
          throw new Error('Resposta invalida do servidor')
        }
      } catch (err) {
        console.error('Import error', err)
        if (err && err.response && err.response.status === 409) {
          const conflictJobId = err.response.data && err.response.data.job_id
          const conflictStarted = !!(err.response.data && err.response.data.started)
          const conflictMessage = conflictStarted
            ? ((err.response.data && err.response.data.message) || 'Ja existe uma importacao em andamento.')
            : 'A importacao anterior foi enfileirada mas nao comecou a ser processada. Tente novamente em alguns minutos; se persistir, a fila do servidor esta parada.'

          this.showWarning(conflictMessage)

          if (conflictStarted && conflictJobId) {
            try { localStorage.setItem('work_schedules_import_job_id', conflictJobId) } catch (e) { console.error(e) }
            this.importJobId = conflictJobId
            this.isImportInBackground = true
            this.importStatus = 'Importação em andamento (em segundo plano)'
            this.startImportPolling(conflictJobId)
          }

          this.isImportModalVisible = false
          this.isImportUploading = false
          return
        }

        const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Erro ao enviar arquivo'
        this.showError(msg)
        this.isImportUploading = false
      }
    },

    summarizeImportReports(reports) {
      const summary = {created: 0, replaced: 0, kept: 0}

      const list = Array.isArray(reports) ? reports : []
      list.forEach(report => {
        if (!report) return
        summary.created += Number(report.success_count) || 0
        summary.replaced += Number(report.replaced_count) || 0
        summary.kept += Number(report.conflict_count) || 0
      })

      return summary
    },

    buildImportSummaryMessage(reports) {
      const {created, replaced, kept} = this.summarizeImportReports(reports)
      const parts = [`${created} escala(s) criada(s)`]

      if (replaced) parts.push(`${replaced} substituida(s)`)
      if (kept) parts.push(`${kept} dia(s) mantido(s) por ja ter escala`)

      return `Importacao concluida. ${parts.join(', ')}. Calendario atualizado.`
    },

    startImportPolling(jobId) {
      const apiBase = this.$store.getters.api
      let delay = 2000
      const maxAttempts = 900
      let attempts = 0

      const poll = async () => {
        attempts++
        try {
          const {data: progResp} = await axios.get(`${apiBase}work_schedules/import/progress/${jobId}`)
          const progress = progResp && progResp.data

          if (progress) {
            this.importProgressPercent = progress.percent || progress.progress || this.importProgressPercent
            this.importStatus = progress.message || (progress.status ? progress.status : this.importStatus)

            if (progress.status === 'completed') {
              try { localStorage.removeItem('work_schedules_import_job_id') } catch (e) { console.error(e) }
              this.isImportInBackground = false
              await this.handleImportCompletion(jobId)
              return
            }

            if (progress.status === 'failed') {
              try { localStorage.removeItem('work_schedules_import_job_id') } catch (e) { console.error(e) }
              this.isImportInBackground = false
              this.showError(progress.message || 'Importacao falhou')
              this.isImportUploading = false
              return
            }
          }
        } catch (err) {
          if (err && err.response && err.response.status === 404) {
            try { localStorage.removeItem('work_schedules_import_job_id') } catch (e) { console.error(e) }
            this.isImportInBackground = false
            this.isImportUploading = false
            this.importStatus = ''
            this.showWarning('Nao foi possivel acompanhar esta importacao (progresso expirado). Envie o arquivo novamente.')
            return
          }

          console.warn('Polling error, tentando novamente...', err)
        }

        if (attempts >= maxAttempts) {
          this.showError('Timeout: importacao levou muito tempo')
          this.isImportUploading = false
          return
        }

        if (attempts % 25 === 0 && delay < 10000) delay += 2000

        setTimeout(poll, delay)
      }

      poll()
    },

    async handleImportCompletion(jobId) {
      try {
        const apiBase = this.$store.getters.api
        const {data: resultsResp} = await axios.get(`${apiBase}work_schedules/import/results/${jobId}`)
        const results = (resultsResp && resultsResp.data) || resultsResp

        if (!results) {
          this.showError('Nao foi possivel recuperar os resultados')
          this.isImportUploading = false
          return
        }

        const reports = results.reports || results

        const errors = []
        ;(reports || []).forEach((r, idx) => {
          if (r && r.results && Array.isArray(r.results.errors)) {
            r.results.errors.forEach(e => {
              const line = e.line || (e.lines && e.lines.length ? e.lines[0] : null) || (r.source_lines && r.source_lines.length ? r.source_lines[0] : null)
              errors.push({line: line || idx + 2, error: e.error || JSON.stringify(e), value: JSON.stringify(e)})
            })
            return
          }

          if (r && r.error) {
            const line = r.line || (r.lines && r.lines.length ? r.lines[0] : null) || (r.source_lines && r.source_lines.length ? r.source_lines[0] : null)
            errors.push({line: line || idx + 2, error: r.error, value: JSON.stringify(r.row || r)})
            return
          }

          if (r && r.status === 'skipped') {
            const line = r.line || (r.lines && r.lines.length ? r.lines[0] : null) || (r.source_lines && r.source_lines.length ? r.source_lines[0] : null)
            errors.push({line: line || idx + 2, error: 'skipped', value: JSON.stringify(r.row || r)})
            return
          }
        })

        this.importResult = JSON.stringify(reports, null, 2)
        this.importErrorList = errors

        if (errors.length) {
          const processed = Array.isArray(reports) ? reports.length : 0
          this.openImportErrorsModal(errors, processed)
        } else {
          this.showSuccess(this.buildImportSummaryMessage(reports))
        }

        this.clearScheduleCache()
        await this.loadSchedules()
      } catch (err) {
        console.error('Erro ao recuperar resultados:', err)
        this.showError('Erro ao recuperar resultados da importacao')
      } finally {
        try { localStorage.removeItem('work_schedules_import_job_id') } catch (e) { console.error(e) }
        this.importProgressPercent = 100
        this.importStatus = 'Concluído'
        this.isImportUploading = false
        this.isImportInBackground = false
      }
    },

    async downloadTemplate() {
      try {
        const urlPrimary = `${this.$store.getters.api}work_schedules/import/template`
        let response
        try {
          response = await axios.get(urlPrimary, { responseType: 'blob' })
        } catch (err) {
          if (err.response && err.response.status === 404) {
            const urlFallback = `${this.$store.getters.api}work_schedules/import/template-public`
            response = await axios.get(urlFallback, { responseType: 'blob' })
          } else {
            throw err
          }
        }
        const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
        let filename = 'template_importacao_escalas.xlsx'
        if (disposition) {
          const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
          if (match && match[1]) {
            filename = match[1].replace(/['"]/g, '')
          }
        }

        const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
      } catch (err) {
        console.error('Download template error', err)
        this.showError('Erro ao baixar template')
      }
    },

    openQuickSchedule(dateStr) {
      if (this.isUserView) return
      this.resetScheduleForm()
      this.resetEmployeeResults()
      this.isQuickSchedule = true
      this.quickScheduleDate = dateStr
      this.newSchedule.month = dateStr.slice(0, 7)
      this.newSchedule.workDays = [parseInt(dateStr.split('-')[2])]
      this.showScheduleModal = true
      this.loadDutyShiftsForDate(dateStr)
    },

    closeScheduleModal() {
      this.showScheduleModal = false
      this.resetScheduleForm()
    },

    closeEventModal() {
      this.showEventModal = false
      this.selectedSwapEvent = null
    },

    closePendingDutyShiftModal() {
      this.showPendingDutyShiftModal = false
      this.selectedSwapEvent = null
    },

    async cancelPendingDutyShift() {
      const allocId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.duty_shift_allocation_id
      if (!allocId) return
      this.cancelingShiftId = allocId
      try {
        await axios.delete(`${this.$store.getters.api}duty_shift_allocations/${allocId}`)
        this.showSuccess('Solicitação cancelada. Vaga disponível novamente.')
        this.closePendingDutyShiftModal()
        this.loadMyDutyShiftAllocations()
        this.loadAvailableDutyShifts()
        this.clearScheduleCache()
        await this.loadSchedules()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao cancelar solicitação.'
        this.showError(msg)
      } finally {
        this.cancelingShiftId = null
      }
    },

    async releaseMySchedule() {
      const scheduleId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.id
      if (!scheduleId) return
      if (!await this.confirm('Confirma a liberação desta escala?')) return
      try {
        await axios.delete(`${this.$store.getters.api}work_schedules/${scheduleId}`)
        this.showSuccess('Escala liberada com sucesso.')
        this.closeEventModal()
        this.schedules = this.schedules.filter(s => s.id !== scheduleId)
        this.$delete(this.releaseRequests, scheduleId)
        this.scheduleCache.clear()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao liberar escala.'
        this.showError(msg)
      }
    },

    openReleaseModal(mode = 'direct') {
      const times = this.eventShiftTimes
      const startTime = (times && times.start) ? String(times.start).slice(0, 5) : '08:00'
      const endTime   = (times && times.end)   ? String(times.end).slice(0, 5)   : '17:00'
      this.releaseSlots = [{ title: '', start_time: startTime, end_time: endTime, vacancies: 1, requirement_ids: [] }]
      this.approvingReleaseRequest = mode === 'approve' ? this.currentEventReleaseRequest : null
      this.approveReleaseForm = { plantao_title: '', plantao_vacancies: 1, plantao_requirement_ids: [], create_plantao: false }
      this.showReleaseModal = true
    },

    addReleaseSlot() {
      const last = this.releaseSlots[this.releaseSlots.length - 1]
      this.releaseSlots.push({
        title: '',
        start_time: last ? last.end_time : '08:00',
        end_time: last ? last.end_time : '17:00',
        vacancies: 1,
        requirement_ids: [],
      })
    },

    removeReleaseSlot(index) {
      if (this.releaseSlots.length > 1) {
        this.releaseSlots.splice(index, 1)
      }
    },

    async submitRelease() {
      const scheduleId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.id
      if (!scheduleId) return

      this.releasingSchedule = true
      try {
        let responseData
        if (this.approvingReleaseRequest) {
          const createPlantao = this.approveReleaseForm.create_plantao
          const { data } = await axios.post(
            `${this.$store.getters.api}work_schedule_release_requests/${this.approvingReleaseRequest.id}/approve`,
            {
              create_plantao:    createPlantao,
              plantao_title:     createPlantao ? (this.approveReleaseForm.plantao_title || null) : null,
              plantao_vacancies: createPlantao ? (this.approveReleaseForm.plantao_vacancies || 1) : null,
            }
          )
          responseData = data
        } else {
          for (const slot of this.releaseSlots) {
            if (!slot.start_time || !slot.end_time) {
              this.showError('Preencha os horários de início e fim de todos os plantões.')
              this.releasingSchedule = false
              return
            }
          }
          const { data } = await axios.post(
            `${this.$store.getters.api}work_schedules/${scheduleId}/release`,
            { slots: this.releaseSlots }
          )
          responseData = data
        }
        this.showSuccess(responseData.message || 'Operação realizada com sucesso.')
        this.showReleaseModal = false
        this.closeEventModal()
        if (this.approvingReleaseRequest) {
          const updatedSchedule = responseData.data || {}
          const approvedScheduleId = this.approvingReleaseRequest.work_schedule_id
          const idx = this.schedules.findIndex(s => s.id === approvedScheduleId)
          if (idx !== -1) {
            this.$set(this.schedules, idx, {
              ...this.schedules[idx],
              shift_start_time: updatedSchedule.shift_start_time ?? this.schedules[idx].shift_start_time,
              shift_end_time:   updatedSchedule.shift_end_time   ?? this.schedules[idx].shift_end_time,
            })
          }
          this.$delete(this.releaseRequests, approvedScheduleId)
          this.scheduleCache.clear()
        } else {
          this.schedules = this.schedules.filter(s => s.id !== scheduleId)
          this.$delete(this.releaseRequests, scheduleId)
          this.scheduleCache.clear()
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao liberar escala.'
        this.showError(msg)
      } finally {
        this.releasingSchedule = false
      }
    },

    _deriveReleaseRequestsFromSchedules() {
      const map = {}
      ;(this.schedules || []).forEach(s => {
        if (s.release_request) map[s.id] = s.release_request
      })
      this.releaseRequests = map
    },

    async loadReleaseRequests() {
      try {
        const user = this.$store.getters.user
        let url = `${this.$store.getters.api}work_schedule_release_requests?month=${this.selectedMonth}`
        if (this.isUserView && user) {
          url += `&user_id=${user.id}`
        }
        const { data } = await axios.get(url)
        const map = {}
        ;(data || []).forEach(rr => { map[rr.work_schedule_id] = rr })
        this.releaseRequests = map
      } catch (e) {
        console.error('Error loading release requests:', e)
      }
    },

    async loadPendingSwaps() {
      try {
        const { data } = await axios.get(`${this.$store.getters.api}work_schedule_swap_requests`)
        const list = (data && data.data) || []
        const map = {}
        list.forEach(r => {
          if (!['pending_peer', 'pending_approval'].includes(r.status)) return
          if (r.work_schedule_id) map[r.work_schedule_id] = r
          if (r.target_work_schedule_id) map[r.target_work_schedule_id] = r
        })
        this.pendingSwaps = map
      } catch (e) {
        this.pendingSwaps = {}
      }
    },

    async loadRequirements() {
      try {
        const { data } = await axios.get(`${this.$store.getters.api}requirements`)
        this.availableRequirements = data.data || data || []
      } catch (e) {
        console.error('Error loading requirements:', e)
      }
    },

    requestRelease() {
      const times = this.eventShiftTimes
      const rawStart = (times && times.start) ? String(times.start).slice(0, 5) : ''
      const rawEnd   = (times && times.end)   ? String(times.end).slice(0, 5)   : ''
      this.requestReleaseForm = {
        release_type:        'full',
        proposed_start_time: rawStart,
        proposed_end_time:   rawEnd,
        reason: '',
      }
      this.showRequestReleaseModal = true
    },

    async submitRequestRelease() {
      const scheduleId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.id
      if (!scheduleId) return
      const isPartial = this.requestReleaseForm.release_type === 'partial'
      if (isPartial && (!this.requestReleaseForm.proposed_start_time || !this.requestReleaseForm.proposed_end_time)) {
        this.showError('Preencha o horário proposto.')
        return
      }
      if (isPartial) {
        
        const times    = this.eventShiftTimes
        const dutyDay  = this.selectedSwapEvent ? this.getEventDateKey(this.selectedSwapEvent.start) : null
        const propStartT = String(this.requestReleaseForm.proposed_start_time || '').slice(0, 5)
        const propEndT   = String(this.requestReleaseForm.proposed_end_time || '').slice(0, 5)

        if (times && dutyDay && propStartT && propEndT) {
          const nextDayStr = () => {
            const d = new Date(dutyDay + 'T00:00:00')
            d.setDate(d.getDate() + 1)
            return d.toISOString().slice(0, 10)
          }
          const schedStart = new Date(`${dutyDay}T${times.start}:00`)
          const schedEnd   = new Date(`${times.nextDay ? nextDayStr() : dutyDay}T${times.end}:00`)
          const propStart  = new Date(`${dutyDay}T${propStartT}:00`)
          const propEnd    = new Date(`${propEndT <= propStartT ? nextDayStr() : dutyDay}T${propEndT}:00`)

          if (propStart < schedStart) {
            this.showError(`O início proposto não pode ser anterior ao início da escala (${times.start}).`)
            return
          }
          if (propEnd > schedEnd) {
            this.showError(`O fim proposto não pode ser posterior ao fim da escala (${times.end}).`)
            return
          }
        }
      }
      if (!this.requestReleaseForm.reason || !this.requestReleaseForm.reason.trim()) {
        this.showError('Preencha o motivo da solicitação.')
        return
      }
      this.submittingRequestRelease = true
      try {
        const payload = {
          work_schedule_id: scheduleId,
          reason:           this.requestReleaseForm.reason.trim(),
        }
        if (isPartial) {
          const s = this.requestReleaseForm.proposed_start_time
          const e = this.requestReleaseForm.proposed_end_time
          payload.proposed_start_time = s.includes('T') ? s.slice(11, 16) : s.slice(0, 5)
          payload.proposed_end_time   = e.includes('T') ? e.slice(11, 16) : e.slice(0, 5)
        }
        const { data: rrResp } = await axios.post(`${this.$store.getters.api}work_schedule_release_requests`, payload)
        this.showSuccess('Solicitação criada. Aguardando aprovação.')
        this.showRequestReleaseModal = false
        this.closeEventModal()
        if (rrResp && rrResp.data) {
          this.$set(this.releaseRequests, scheduleId, rrResp.data)
        } else {
          await this.loadReleaseRequests()
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao criar solicitação.'
        this.showError(msg)
      } finally {
        this.submittingRequestRelease = false
      }
    },

    async cancelReleaseRequest() {
      const rr = this.currentEventReleaseRequest
      if (!rr) return
      if (!await this.confirm('Confirma o cancelamento da solicitação de liberação?')) return
      try {
        await axios.delete(`${this.$store.getters.api}work_schedule_release_requests/${rr.id}`)
        this.showSuccess('Solicitação cancelada.')
        this.closeEventModal()
        this.$delete(this.releaseRequests, rr.work_schedule_id)
        this.scheduleCache.clear()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao cancelar solicitação.'
        this.showError(msg)
      }
    },

    async rejectReleaseRequest() {
      const rr = this.currentEventReleaseRequest
      if (!rr) return
      if (!await this.confirm('Rejeitar a solicitação de liberação desta escala?')) return
      try {
        await axios.post(`${this.$store.getters.api}work_schedule_release_requests/${rr.id}/reject`)
        this.showSuccess('Solicitação rejeitada.')
        this.closeEventModal()
        this.$set(this.releaseRequests, rr.work_schedule_id, { ...rr, status: 'rejected' })
        this.scheduleCache.clear()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao rejeitar solicitação.'
        this.showError(msg)
      }
    },

    openEditScheduleModal() {
      const ext = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps

      const dutyDay = this.selectedSwapEvent
        ? this.getEventDateKey(this.selectedSwapEvent.start)
        : null

      const toDatetimeLocal = (val, fallbackDay, refTimeStr) => {
        if (!val) return fallbackDay ? fallbackDay + 'T00:00' : ''
        const str = String(val)
        if (str.includes(' ') && str.length > 10) return str.replace(' ', 'T').slice(0, 16)
        if (str.includes('T') && str.length > 10) return str.slice(0, 16)
        const time = str.slice(0, 5)
        if (refTimeStr && fallbackDay) {
          const startMins = this.timeStrToMinutes(refTimeStr.slice(0, 5))
          const thisMins  = this.timeStrToMinutes(time)
          if (thisMins <= startMins) {
            const d = new Date(fallbackDay + 'T00:00:00')
            d.setDate(d.getDate() + 1)
            return d.toISOString().split('T')[0] + 'T' + time
          }
        }
        return (fallbackDay || '') + 'T' + time
      }

      const rawStart = ext && ext.shift_start_time ? String(ext.shift_start_time) : ''
      const rawEnd   = ext && ext.shift_end_time   ? String(ext.shift_end_time)   : ''
      const startDt  = toDatetimeLocal(rawStart, dutyDay, null)
      const startRef = startDt ? startDt.slice(11, 16) : null
      const endDt    = toDatetimeLocal(rawEnd, dutyDay, startRef)

      this.editScheduleForm = {
        shift_start_time:        startDt,
        shift_end_time:          endDt,
        is_overtime:             !!(ext && ext.is_overtime),
        plantao_title:           '',
        plantao_vacancies:       1,
        plantao_requirement_ids: [],
        create_plantao:          true,
      }
      this.showEditRequirements = false
      this.showEditScheduleModal = true
    },

    async submitEditSchedule() {
      const scheduleId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.id
      if (!scheduleId) return
      if (!this.editScheduleForm.shift_start_time || !this.editScheduleForm.shift_end_time) {
        this.showError('Preencha o horário de início e fim.')
        return
      }
      this.savingEditSchedule = true
      try {
        const payload = { ...this.editScheduleForm }
        if (!payload.create_plantao) payload.plantao_title = null
        const { data } = await axios.patch(
          `${this.$store.getters.api}work_schedules/${scheduleId}/edit-times`,
          payload
        )
        this.showSuccess(data.message || 'Escala atualizada com sucesso.')
        this.showEditScheduleModal = false
        this.closeEventModal()
        const idx = this.schedules.findIndex(s => s.id === scheduleId)
        const updated = data.data || {}
        if (idx !== -1) {
          this.$set(this.schedules, idx, {
            ...this.schedules[idx],
            duty_day:         updated.duty_day         ?? this.schedules[idx].duty_day,
            shift_start_time: updated.shift_start_time ?? this.schedules[idx].shift_start_time,
            shift_end_time:   updated.shift_end_time   ?? this.schedules[idx].shift_end_time,
            is_overtime:      updated.is_overtime      ?? this.schedules[idx].is_overtime,
          })
          this.scheduleCache.clear()
        } else {
          this.clearScheduleCache()
          await this.loadSchedules()
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao salvar.'
        this.showError(msg)
      } finally {
        this.savingEditSchedule = false
      }
    },

    async releaseApprovedDutyShift() {
      const allocId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.duty_shift_allocation_id
      if (!allocId) return
      if (!await this.confirm('Confirma a liberação do plantão? Você será removido e a vaga ficará disponível novamente.')) return
      this.cancelingShiftId = allocId
      try {
        await axios.delete(`${this.$store.getters.api}duty_shift_allocations/${allocId}`)
        this.showSuccess('Plantão liberado com sucesso. Vaga disponível novamente.')
        this.closeEventModal()
        this.loadMyDutyShiftAllocations()
        this.loadAvailableDutyShifts()
        this.clearScheduleCache()
        await this.loadSchedules()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao liberar plantão.'
        this.showError(msg)
      } finally {
        this.cancelingShiftId = null
      }
    },

    async releaseOwnSchedule() {
      if (!await this.confirm('Confirmar liberação da escala? Ela será removida do seu calendário.')) return
      const scheduleId = this.selectedSwapEvent && this.selectedSwapEvent.extendedProps.id
      if (!scheduleId) return
      this.releasingSchedule = true
      try {
        this.schedules = this.schedules.filter(s => s.id !== scheduleId)
        this.closeEventModal()
        await axios.delete(`${this.$store.getters.api}work_schedules/${scheduleId}`)
        this.clearScheduleCache()
        await this.loadSchedules()
        this.showSuccess('Escala liberada com sucesso!')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao liberar escala.'
        this.showError(msg)
        await this.loadSchedules()
      } finally {
        this.releasingSchedule = false
      }
    },

    async openRequestShiftModal() {
      this.requestShiftMonth = this.selectedMonth
      this.requestSelectedDate = ''
      this.requestSelectedDutyShiftId = null
      this.showRequestShiftModal = true
      if (!this.availableDutyShifts.length) {
        await this.loadAvailableDutyShifts()
      }
    },

    onRequestMonthChange() {
      this.requestSelectedDate = ''
      this.requestSelectedDutyShiftId = null
    },

    getRequestMonthParts() {
      const rawMonth = this.requestShiftMonth || this.selectedMonth
      const isValid = /^\d{4}-\d{2}$/.test(rawMonth)
      const safeMonth = isValid ? rawMonth : this.selectedMonth

      const [y, m] = safeMonth.split('-')
      const year = Number(y)
      const monthIndex = Number(m) - 1

      return [year, monthIndex]
    },

    selectRequestDate(dateKey) {
      this.requestSelectedDate = dateKey
      this.requestSelectedDutyShiftId = null
    },

    closeRequestShiftModal() {
      this.showRequestShiftModal = false
      this.requestSelectedDate = ''
      this.requestSelectedDutyShiftId = null
    },

    async submitDutyShiftRequest() {
      if (!this.requestSelectedDutyShiftId) {
        this.showError('Selecione um plantão para solicitar.')
        return
      }
      this.swapping = true
      try {
        await axios.post(`${this.$store.getters.api}duty_shift_allocations`, {
          duty_shift_id: this.requestSelectedDutyShiftId,
        })
        this.closeRequestShiftModal()
        this.loadAvailableDutyShifts()
        this.loadMyDutyShiftAllocations()
        this.clearScheduleCache()
        await this.loadSchedules()
        this.showSuccess('Solicitação de plantão enviada com sucesso!')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao solicitar plantão.'
        this.showError(msg)
      } finally {
        this.swapping = false
      }
    },

    calcRemainingSlot(oldStart, oldEnd, newStart, newEnd, dutyDayStr) {
      const oStartMin = this.timeStrToMinutes(oldStart)
      let oEndMin = this.timeStrToMinutes(oldEnd)
      if (oEndMin <= oStartMin) oEndMin += 24 * 60

      const nStartMin = this.timeStrToMinutes(newStart)
      let nEndMin = this.timeStrToMinutes(newEnd)
      if (nEndMin <= nStartMin) nEndMin += 24 * 60

      if (nEndMin >= oEndMin) return null

      const result = {
        start: this.minutesToTimeStr(nEndMin % (24 * 60)),
        end:   this.minutesToTimeStr(oEndMin % (24 * 60)),
      }

      if (dutyDayStr) {
        const sameDay = this.formatDate(dutyDayStr)
        const nd = new Date(dutyDayStr + 'T00:00:00')
        nd.setDate(nd.getDate() + 1)
        const nextDay = this.formatDate(nd.toISOString().split('T')[0])
        result.startDate = nEndMin >= 24 * 60 ? nextDay : sameDay
        result.endDate   = oEndMin >= 24 * 60 ? nextDay : sameDay
      }

      return result
    },

    minutesToTimeStr(totalMinutes) {
      const h = Math.floor(totalMinutes / 60) % 24
      const m = totalMinutes % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },

    resetScheduleForm() {
      this.newSchedule = {month: this.selectedMonth, employeeId: null, departmentId: '', workDays: [], isOvertime: false, dutyShiftId: null}
      this.dutyShiftsOnSelectedDate = []
      this.isQuickSchedule = false
      this.quickScheduleDate = null
    },

    toggleWorkDay(day) {
      const index = this.newSchedule.workDays.indexOf(day)
      index > -1 ?
          this.newSchedule.workDays.splice(index, 1) :
          this.newSchedule.workDays.push(day)
      this.newSchedule.dutyShiftId = null
      this.loadDutyShiftsForDate(this.selectedSingleDay)
    },

    async loadDutyShiftsForDate(dateStr) {
      this.newSchedule.dutyShiftId = null
      if (!dateStr) {
        this.dutyShiftsOnSelectedDate = []
        return
      }
      this.loadingDutyShifts = true
      try {
        const res = await axios.get(`${this.$store.getters.api}duty_shifts`, {
          params: {from: dateStr, to: dateStr + 'T23:59:59'}
        })
        this.dutyShiftsOnSelectedDate = (res.data.data || []).filter(s => s.available_vacancies > 0)
      } catch (e) {
        this.dutyShiftsOnSelectedDate = []
      } finally {
        this.loadingDutyShifts = false
      }
    },

    async confirmDeleteEvent() {
      if (this.isUserView) return
      if (!await this.confirm('Excluir esta escala?')) return

      try {
        const scheduleId = this.selectedSwapEvent.extendedProps.id
        const removedSchedule = this.schedules.find(s => s.id === scheduleId)

        this.schedules = this.schedules.filter(s => s.id !== scheduleId)
        this.closeEventModal()

        try {
          await axios.delete(`${this.$store.getters.api}work_schedules/${scheduleId}`)
          this.clearScheduleCache()
          this.showSuccess('Escala excluída!')
        } catch (deleteErr) {
          console.error(deleteErr)
          if (removedSchedule) {
            this.schedules.push(removedSchedule)
          }
          await this.loadSchedules()
          this.showError('Erro ao excluir escala')
        }
      } catch (error) {
        console.error(error)
        this.showError('Erro ao excluir escala')
      }
    },

    dismissImportBanner() {
      this.isImportInBackground = false
      this.importStatus = ''
      try { localStorage.removeItem('work_schedules_import_job_id') } catch (e) { console.error(e) }
    },

    goToPrev() { this.$refs.fullCalendar.getApi().prev() },
    goToNext() { this.$refs.fullCalendar.getApi().next() },
    goToToday() { this.$refs.fullCalendar.getApi().today() },

    changeView() { this.$refs.fullCalendar.getApi().changeView(this.calendarView) },

    navigateCalendar() {
      if (!this.$refs.fullCalendar) return
      const [year, month] = this.selectedMonth.split('-')
      this.$refs.fullCalendar.getApi().gotoDate(new Date(parseInt(year), parseInt(month) - 1, 1))
    },

    filterSchedules() { this.loadSchedules() },

    getEmployeeById(id) { return this.employees.find(emp => emp.id == id) },

    openMultipleEventsModal(date) {
      const dateKey = this.getEventDateKey(date)
      this.multipleEventsData = this.calendarEvents.filter(event => this.getEventDateKey(event.start) === dateKey)
      this.multipleEventsModalTitle = `Escalas do dia ${this.formatDate(dateKey)} (${this.multipleEventsData.length})`
      this.multipleEventsSearch = ''
      this.showMultipleEventsModal = true
    },

    closeMultipleEventsModal() {
      this.showMultipleEventsModal = false
      this.multipleEventsData = []
      this.multipleEventsSearch = ''
    },

    selectEventFromList(event) {
      this.selectedSwapEvent = {
        start: event.start,
        extendedProps: event.extendedProps,
      }
      this.closeMultipleEventsModal()
      this.showEventModal = true
    },

    getEventDateKey(dateInput) {
      if (!dateInput) return ''
      if (dateInput instanceof Date) {
        const year = dateInput.getFullYear()
        const month = String(dateInput.getMonth() + 1).padStart(2, '0')
        const day = String(dateInput.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      const raw = String(dateInput).trim()
      if (!raw) return ''

      const normalized = raw.replace(' ', 'T')
      if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
        return normalized.slice(0, 10)
      }

      const parsed = new Date(normalized)
      if (!isNaN(parsed.getTime())) {
        const year = parsed.getFullYear()
        const month = String(parsed.getMonth() + 1).padStart(2, '0')
        const day = String(parsed.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      return raw.split('T')[0].split(' ')[0]
    },

    getEmployeeNameFromEvent(event) {
      const workingUser = event && event.extendedProps && event.extendedProps.workingUser
      if (workingUser && workingUser.name) return workingUser.name
      if (event && event.extendedProps && event.extendedProps.is_swapped && event.extendedProps.swapped_user_name) return event.extendedProps.swapped_user_name
      if (event && event.extendedProps && event.extendedProps.user_name) return event.extendedProps.user_name
      return `ID: ${(event && event.extendedProps && event.extendedProps.user_id) || 'N/A'}`
    },

    getOriginalEmployeeNameFromEventData(event) {
      const originalUser = event && event.extendedProps && event.extendedProps.originalUser
      if (originalUser && originalUser.name) return originalUser.name
      if (event && event.extendedProps && event.extendedProps.user_name) return event.extendedProps.user_name
      return `ID: ${(event && event.extendedProps && event.extendedProps.user_id) || 'N/A'}`
    },

    getDepartmentNameFromEventData(event) {
      const departmentId = event && event.extendedProps && event.extendedProps.department_id
      if (!departmentId) return 'N/A'
      const dept = this.departments.find(d => d.id == departmentId)
      return (dept && dept.title) || (dept && dept.name) || 'N/A'
    },

    getDisplayEmployeeName() {
      if (!this.selectedSwapEvent || !this.selectedSwapEvent.extendedProps) return 'N/A'

      const workingUser = this.selectedSwapEvent.extendedProps.workingUser
      if (workingUser && workingUser.name) return workingUser.name

      if (this.selectedSwapEvent.extendedProps.is_swapped && this.selectedSwapEvent.extendedProps.swapped_user_name) {
        return this.selectedSwapEvent.extendedProps.swapped_user_name
      }

      if (this.selectedSwapEvent.extendedProps.user_name) {
        return this.selectedSwapEvent.extendedProps.user_name
      }

      const userId = this.selectedSwapEvent.extendedProps.is_swapped
        ? this.selectedSwapEvent.extendedProps.swapped_user_id
        : this.selectedSwapEvent.extendedProps.user_id

      return `ID: ${userId}`
    },

    getOriginalEmployeeName() {
      if (!this.selectedSwapEvent || !this.selectedSwapEvent.extendedProps || !this.selectedSwapEvent.extendedProps.user_id) return 'N/A'

      const originalUser = this.selectedSwapEvent.extendedProps.originalUser
      if (originalUser && originalUser.name) return originalUser.name

      if (this.selectedSwapEvent.extendedProps.user_name) return this.selectedSwapEvent.extendedProps.user_name

      return `ID: ${this.selectedSwapEvent.extendedProps.user_id}`
    },

    getDepartmentNameFromEvent() {
      if (this.isUserView) {
        const currentUser = this.$store.getters.user
        if (currentUser && currentUser.department_id) {
          const dept = this.departments.find(d => d.id == currentUser.department_id)
          return (dept && dept.title) || (dept && dept.name) || 'N/A'
        }
        return 'N/A'
      }

      if (!this.selectedSwapEvent || !this.selectedSwapEvent.extendedProps || !this.selectedSwapEvent.extendedProps.department_id) return 'N/A'

      const dept = this.departments.find(d => d.id == this.selectedSwapEvent.extendedProps.department_id)
      return (dept && dept.title) || (dept && dept.name) || 'N/A'
    },

    extractTime(val) {
      if (!val) return null
      const str = String(val)
      const spaceIdx = str.indexOf(' ')
      if (spaceIdx > 5) return str.slice(spaceIdx + 1, spaceIdx + 6)
      const tIdx = str.indexOf('T')
      if (tIdx > 5) return str.slice(tIdx + 1, tIdx + 6)
      return str.slice(0, 5)
    },

    formatDate(dateInput) {
      if (!dateInput) return 'N/A'

      let dateStr

      if (dateInput instanceof Date) {
        dateStr = dateInput.toISOString().split('T')[0]
      } else if (typeof dateInput === 'string') {
        dateStr = dateInput
      } else {
        return 'N/A'
      }

      const [year, month, day] = dateStr.split('-')
      return `${day}/${month}/${year}`
    },

    showSuccess(msg) { typeof swal !== 'undefined' ? swal('Sucesso', msg, 'success') : alert(msg) },
    showWarning(msg) { typeof swal !== 'undefined' ? swal('Atencao', msg, 'warning') : alert(msg) },
    showError(msg) { typeof swal !== 'undefined' ? swal('Erro', msg, 'error') : alert(msg) },

    buildImportValidationMessage(errors, processedCount = 0) {
      const toText = (item) => `${(item && item.error) || ''} ${(item && item.value) || ''}`.toLowerCase()
      const allText = (errors || []).map(toText).join(' ')

      const tips = []
      if (allText.includes('cpf')) tips.push('verifique CPF (ex: 000.000.000-00)')
      if (allText.includes('competencia') || allText.includes('month') || allText.includes('mes')) tips.push('verifique COMPETENCIA (ex: 04/2026 ou abril/2026)')
      if (allText.includes('usuario') || allText.includes('user') || allText.includes('colaborador')) tips.push('confira se o colaborador existe na base')
      if (!tips.length) tips.push('revise formatação e colunas obrigatórias (CPF, COMPETENCIA, DIAS)')

      const problemLines = (errors || [])
        .filter(e => e && (e.line || (e.lines && e.lines.length)))
        .slice(0, 6)
        .map(e => {
          const lineNum = e.line || (Array.isArray(e.lines) ? e.lines[0] : '?')
          const msg = e.error || (typeof e === 'string' ? e : (e && e.value ? `${e.value}` : JSON.stringify(e)))
          return `Linha ${lineNum}: ${msg}`
        })
        .join('\n')

      const processedLine = processedCount > 0 ? `\nRegistros processados: ${processedCount}.` : ''

      const suggestions = tips.slice(0, 3).map((t, i) => `${i + 1}. ${t}`).join('\n')

      const header = 'Por favor, valide estas linhas e possíveis erros:'
      const linesSection = problemLines ? `${problemLines}\n\n` : ''

      return `${header}\n\n${linesSection}Sugestoes:\n${suggestions}${processedLine}`
    },

    async confirm(msg) {
      return typeof swal !== 'undefined' ?
          new Promise(resolve => swal({
            title: 'Confirmar',
            text: msg,
            icon: 'warning',
            buttons: ['Cancelar', 'Confirmar']
          }).then(resolve)) :
          confirm(msg)
    },

    async loadMyDutyShiftAllocations() {
      try {
        const res = await axios.get(`${this.$store.getters.api}duty_shift_allocations/mine`)
        this.myDutyShiftAllocations = res.data.data || []
      } catch (e) {
        this.myDutyShiftAllocations = []
        console.error('Error loading my duty shift allocations:', e)
      }
    },

    async loadAvailableDutyShifts() {
      this.loadingDutyShifts = true
      try {
        const res = await axios.get(`${this.$store.getters.api}duty_shifts/available`)
        this.availableDutyShifts = res.data.data || []
      } catch (e) {
        this.availableDutyShifts = []
        console.error('Error loading available duty shifts:', e)
      } finally {
        this.loadingDutyShifts = false
      }
    },

    async requestDutyShift(shift) {
      this.requestingShiftId = shift.id
      try {
        const res = await axios.post(`${this.$store.getters.api}duty_shift_allocations`, {
          duty_shift_id: shift.id,
        })
        this.showSuccess(res.data.message || 'Solicitação enviada.')
        this.loadAvailableDutyShifts()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao solicitar plantão.'
        this.showError(msg)
      } finally {
        this.requestingShiftId = null
      }
    },

    async cancelDutyShiftAllocation(shift) {
      if (!confirm('Deseja sair deste plantão?')) return
      this.cancelingShiftId = shift.id
      try {
        const found = this.availableDutyShifts.find(s => s.id === shift.id)
        const myAlloc = this.myDutyShiftAllocations.find(a => a.duty_shift_id === shift.id)
        const allocId = (found && found.allocation_id) || (myAlloc && myAlloc.id)
        if (!allocId) {
          this.showWarning('Não foi possível identificar sua alocação. Tente novamente.')
          this.loadAvailableDutyShifts()
          this.loadMyDutyShiftAllocations()
          return
        }
        await axios.delete(`${this.$store.getters.api}duty_shift_allocations/${allocId}`)
        this.showSuccess('Você saiu do plantão. Vaga disponível novamente.')
        this.loadAvailableDutyShifts()
        this.loadMyDutyShiftAllocations()
        this.loadSchedules()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao cancelar alocação.'
        this.showError(msg)
      } finally {
        this.cancelingShiftId = null
      }
    },

    formatDutyShiftDate(value) {
      if (!value) return ''
      try {
        const d = new Date(value)
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
          + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      } catch (e) { return value }
    },

    formatDutyShiftTime(value) {
      if (!value) return ''
      try {
        return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      } catch (e) { return value }
    },
  }
}
</script>

<style scoped>
/* styles unchanged */
.day-item-compact:hover {
  border-color: #007bff !important;
  background: rgba(0, 123, 255, 0.1) !important;
  transform: scale(1.05);
}

.day-item-compact.selected {
  background: #007bff !important;
  border-color: #0056b3 !important;
  color: white !important;
}

.form-control-plaintext {
  padding: 0.375rem 0;
  margin-bottom: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #495057;
  background-color: transparent;
  border: solid transparent;
  border-width: 1px 0;
  min-height: calc(1.5em + 0.75rem + 2px);
}

.form-group label {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  color: #6c757d;
}

.required {
  color: red;
}

.period-display {
  text-transform: capitalize;
}

::v-deep .fc {
  font-family: inherit;
}

::v-deep .fc-popover {
  display: none !important;
}

::v-deep .fc-event {
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 6px;
  font-weight: 500;
  font-size: 12px;
  border: none;
  transition: all 0.2s ease;
}

::v-deep .fc-event:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

::v-deep .fc-daygrid-day {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

::v-deep .fc-daygrid-day:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

::v-deep .fc-day-today {
  background-color: rgba(0, 123, 255, 0.1) !important;
}

::v-deep .fc-daygrid-day-number {
  font-weight: 600;
}

::v-deep .fc-daygrid-event {
  font-size: 11px;
}

::v-deep .fc-event-title {
  font-size: 11px;
}

@media (max-width: 768px) {
  .nav-controls {
    flex-wrap: wrap;
    justify-content: center !important;
  }

  .legend {
    justify-content: center !important;
    flex-wrap: wrap;
    margin-top: 10px;
  }

  .days-selector-compact {
    grid-template-columns: repeat(7, 1fr) !important;
    gap: 3px !important;
    padding: 8px !important;
  }

  .day-item-compact {
    font-size: 11px !important;
    height: 25px !important;
  }

  .custom-modal-overlay {
    padding: 10px;
  }

  .request-shift-modal-dialog {
    max-width: 100%;
  }

  .request-month-picker {
    max-width: 100%;
  }

  .request-days-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 6px;
  }

  .request-day-btn {
    padding: 0.25rem 0.3rem;
    font-size: 0.8rem;
  }
}

.custom-modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  padding: 20px;
}

.request-shift-modal-dialog {
  max-width: 680px;
  width: 100%;
}

.request-month-picker {
  max-width: 320px;
  margin: 0 auto;
}

.request-days-wrap {
  max-height: none;
  overflow: visible;
}

.request-days-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 8px;
}

.request-day-btn {
  min-width: 0;
}
.import-modal-dialog {
  max-width: 520px;
  width: 100%;
}
.import-modal-content {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}

.custom-file-input {
  display: block;
  position: relative;
  border: 1px dashed #e2e6ea;
  padding: 12px;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}
.custom-file-input input[type=file] {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 5;
  pointer-events: auto;
}
.file-ui {
  display: flex;
  align-items: center;
  gap: 12px;
}
.btn-select {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #007bff;
  color: #fff;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.95rem;
  position: relative;
  z-index: 2;
}
.btn-select i { font-size: 14px; }
.btn-select:hover { background: #0069d9; }
.custom-file-input .file-name { font-size: 0.95rem; color: #6c757d; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-hint { display: block; margin-top: 8px; color: #868e96; font-size: 0.85rem; }
.file-dropzone { min-height: 88px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; border: 1px dashed #d6dbe0; border-radius: 8px; padding: 12px; background: linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%); text-align: center; color: #495057; }
.drop-icon { font-size: 28px; color: #6c757d; }
.drop-text { font-size: 0.98rem; color: #495057; font-weight: 500; }
.file-dropzone .file-name { font-size: 0.9rem; color: #6c757d; }
.report-pre { max-height: 200px; overflow: auto; background: #f8f9fa; padding: 10px; border-radius: 6px; white-space: pre-wrap; }
.import-errors { max-height: 150px; overflow: auto; padding-left: 18px; }
.import-modal-footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 16px; }
.import-modal-footer .left-actions { display: flex; align-items: center; }
.import-modal-footer .right-actions { display: flex; align-items: center; }

.accordion-enter-active {
  transition: max-height 0.32s ease, opacity 0.28s ease;
  overflow: hidden;
  max-height: 350px;
}
.accordion-leave-active {
  transition: max-height 0.22s ease, opacity 0.18s ease;
  overflow: hidden;
  max-height: 350px;
}
.accordion-enter,
.accordion-leave-to {
  max-height: 0 !important;
  opacity: 0;
}
</style>
